import React, { useEffect, useRef, useState, useCallback } from 'react';
import { BackPill } from './BackPill';
import { ChevronLeft } from 'lucide-react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
// (No icon imports — header and back buttons removed by design.)
import html2canvas from 'html2canvas';
import { BACKEND_URL } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { useHistoryClose } from '../../hooks/useHistoryClose';
import { getAudioCtx } from '../../utils/audioCore';

/* ═══════════════════════════════════════════════════════════════════
   THE MANIFEST — Genie's Lamp v1 (Cinematic Ritual)

   FLOW
   ──────
   1. awakening (~6s): tiny white orb pulses in centre → breathing beam
      cycles through 8 category colors (one per breath) → final gold
      beam expands and fills the entire screen.
   2. lamp_appear (~2s): from centre of the divine beam, an ornate gold
      Aladdin-style oil lamp materialises with deep 3D shadow + bloom.
   3. lamp_idle: lamp floats with subtle breathing — tap to summon.
   4. smoke (~2.5s): violet/blue smoke billows from the lamp's spout
      using SVG turbulence (real VFX, not cartoon).
   5. genie_appear: tall translucent luminous silhouette rises from the
      smoke. No face — divine presence (premium, not Disney).
   6. dialogue: chat-style Q&A with profile avatars:
        • Genie asks (genie sigil avatar on the left)
        • User answers (user's profile picture on the left)
      4 sequential questions: wish → action → feeling → who-to-tell.
   7. scroll: a parchment unfurls listing the four answers in gold.
   8. pilgrimage (~3s): the scroll rolls up → dissolves into glowing
      smoke particles → smoke streams from the user's avatar → through
      the lamp → up into the divine beam at the top of the screen.
   9. sealed: the lamp glows with the user's wish encoded, "Sealed in
      your gallery" line, then close.

   ZERO COST
   ─────────
   • Pure SVG + CSS + Framer Motion — no R3F, no external images.
   • Web Audio API procedural sounds: chime sweep, brass ring, smoke
     whisper, parchment crackle, soft wind.
   ═══════════════════════════════════════════════════════════════════ */

const GOLD       = '#D4AF37';
const GOLD_LIGHT = '#F5E6A0';
const GOLD_DARK  = '#8B6B2F';

/* The 8 category colors that cycle through the awakening beam. Mirrors
   CATEGORY_STYLE in UserProfile.js so the orb feels native to the app. */
const CATEGORY_COLORS = [
  '#D4AF37', // fashion — imperial gold
  '#E8943C', // food — amber copper
  '#0F52BA', // explore — sapphire
  '#DC143C', // souls — royal ruby
  '#10B981', // sporty — emerald
  '#E5C44D', // success — pale gold
  '#8B5CF6', // legacy — amethyst
  '#1E6FE3', // art — imperial blue
];

const QUESTIONS = [
  'Ask your wish...',
  'If you have it now — what would you do?',
  'If you have it now — how would you feel?',
  'If you have it now — who do you call and tell?',
];

/* ─────────────── Web Audio API — procedural premium sounds ─────────────── */

const useAudio = () => {
  const ctxRef = useRef(null);
  const droneRef = useRef(null);
  const getCtx = () => {
    ctxRef.current = getAudioCtx();
    return ctxRef.current;
  };

  /* Crystal chime — soft bell that swells as the orb is born. */
  const chime = (freq = 880, duration = 1.2, gain = 0.18) => {
    const ctx = getCtx(); if (!ctx) return;
    const t = ctx.currentTime;
    [freq, freq * 1.5, freq * 2.0].forEach((f, i) => {
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.type = 'sine'; o.frequency.value = f;
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(gain / (i + 1), t + 0.30);   // longer attack removes tin-tin click
      g.gain.exponentialRampToValueAtTime(0.0001, t + duration);
      o.connect(g).connect(ctx.destination); o.start(t); o.stop(t + duration + 0.02);
    });
  };

  /* Swell — soft rising drone that grows from silence to a warm chord.
     Matches the ball emerging from the centre of the screen (awakening).
     No hard attacks, no metallic ring — pure sine-based cinematic bloom. */
  const swell = (duration = 3.6) => {
    const ctx = getCtx(); if (!ctx) return;
    const t = ctx.currentTime;
    /* C major 7 low voicing — warm, ceremonial, non-cliché */
    [130.81, 164.81, 196.00, 246.94].forEach((f, i) => {
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.type = 'sine';
      o.frequency.setValueAtTime(f * 0.985, t);
      o.frequency.linearRampToValueAtTime(f, t + duration * 0.35);   // slow settle
      const peak = 0.14 - i * 0.02;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(peak, t + duration * 0.55); // long swell in
      g.gain.linearRampToValueAtTime(peak * 0.7, t + duration * 0.85);
      g.gain.exponentialRampToValueAtTime(0.0001, t + duration);      // silky fade
      o.connect(g).connect(ctx.destination);
      o.start(t); o.stop(t + duration + 0.05);
    });
    /* Airy sparkle on top — subtle, ONE soft partial only, no clicks */
    const shimmer = ctx.createOscillator();
    const sg = ctx.createGain();
    shimmer.type = 'sine';
    shimmer.frequency.setValueAtTime(987.77, t);          // B5
    sg.gain.setValueAtTime(0.0001, t);
    sg.gain.exponentialRampToValueAtTime(0.05, t + duration * 0.60);
    sg.gain.exponentialRampToValueAtTime(0.0001, t + duration);
    shimmer.connect(sg).connect(ctx.destination);
    shimmer.start(t); shimmer.stop(t + duration + 0.05);
  };

  /* ── ELITE COSMIC AMBIENT ENGINE ──
     A continuous, never-stopping rich/regal/cinematic space drift.
     Layers:
       1. Sub-bass swell (very slow LFO)
       2. Lush chord progression cycling every ~24s through:
            Cmaj7 → Am9 → Fmaj9 → Em11
       3. Choir "ahh" pad (filtered saw, slow attack)
       4. Crystal bell tinkles (sine harmonics, sparse irregular)
       5. Glass arpeggio (triangle, panned slow L↔R)
       6. Three-tap delay reverb (0.4 / 0.7 / 1.1 s)
       7. Stereo panner LFO on melody voices
       8. Sidechain-style breathing gain LFO on master (8s cycle)
       9. Slow resonant lowpass sweep (300 → 1800 Hz over 16s)
     Pure procedural, no external files, zero cost. */
  const startMelody = () => {
    const ctx = getCtx(); if (!ctx) return;
    if (droneRef.current) return;
    const t = ctx.currentTime;

    /* ── Master + breathing gain ── */
    const master = ctx.createGain();
    master.gain.value = 0;
    master.gain.linearRampToValueAtTime(0.26, t + 2.0);
    master.connect(ctx.destination);
    const breathLfo  = ctx.createOscillator();
    const breathGain = ctx.createGain();
    breathLfo.frequency.value = 0.125;          // 8-second cycle
    breathGain.gain.value     = 0.06;
    breathLfo.connect(breathGain).connect(master.gain);
    breathLfo.start(t);

    /* ── 3-tap "cathedral in space" reverb network ── */
    const wet = ctx.createGain(); wet.gain.value = 0.75;
    const dry = ctx.createGain(); dry.gain.value = 0.55;
    const tap = (delaySecs, fb) => {
      const d = ctx.createDelay(2.2);
      d.delayTime.value = delaySecs;
      const f = ctx.createGain(); f.gain.value = fb;
      d.connect(f).connect(d);
      d.connect(wet);
      return d;
    };
    const tap1 = tap(0.40, 0.45);
    const tap2 = tap(0.70, 0.52);
    const tap3 = tap(1.10, 0.40);
    wet.connect(master);
    dry.connect(master);

    /* ── Master resonant lowpass — slowly opens over 16s ── */
    const masterLpf = ctx.createBiquadFilter();
    masterLpf.type = 'lowpass';
    masterLpf.frequency.setValueAtTime(300, t);
    masterLpf.frequency.linearRampToValueAtTime(1800, t + 16);
    masterLpf.Q.value = 1.2;
    masterLpf.connect(dry);
    masterLpf.connect(tap1);
    masterLpf.connect(tap2);
    masterLpf.connect(tap3);

    /* ── Sub-bass swell (independent of chord) ── */
    const subOsc = ctx.createOscillator();
    const subGain = ctx.createGain();
    subOsc.type = 'sine';
    subOsc.frequency.value = 65.41;   // C2
    subGain.gain.value = 0.10;
    const subLfo = ctx.createOscillator();
    const subLfoG = ctx.createGain();
    subLfo.frequency.value = 0.06;
    subLfoG.gain.value = 0.05;
    subLfo.connect(subLfoG).connect(subGain.gain);
    subOsc.connect(subGain).connect(masterLpf);
    subOsc.start(t); subLfo.start(t);

    /* ── Lush chord progression — 4 chords, 24s loop ──
         Each chord = root + third + fifth + seventh + ninth, soft saw
         choir pad (lowpassed), held continuously, crossfaded between
         chord changes for seamless drift. */
    const CHORDS = [
      // Cmaj7  (C, E, G, B, D)
      [130.81, 164.81, 196.00, 246.94, 293.66],
      // Am9    (A, C, E, G, B)
      [110.00, 130.81, 164.81, 196.00, 246.94],
      // Fmaj9  (F, A, C, E, G)
      [ 87.31, 110.00, 130.81, 164.81, 196.00],
      // Em11   (E, G, B, D, A)
      [ 82.41,  98.00, 123.47, 146.83, 220.00],
    ];
    const chordVoices = CHORDS[0].map(() => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = 'sawtooth';
      g.gain.value = 0.045;
      const filt = ctx.createBiquadFilter();
      filt.type = 'lowpass'; filt.frequency.value = 950; filt.Q.value = 0.6;
      o.connect(filt).connect(g).connect(masterLpf);
      o.start(t);
      return { o, g };
    });
    const setChord = (idx, when) => {
      const target = CHORDS[idx % CHORDS.length];
      chordVoices.forEach((v, i) => {
        v.o.frequency.setTargetAtTime(target[i], when, 1.8);   // smooth glide
      });
    };
    setChord(0, t);
    let chordIdx = 0;
    const chordTimer = setInterval(() => {
      chordIdx += 1;
      setChord(chordIdx, ctx.currentTime);
    }, 6000);   // 4 chords × 6s = 24s loop

    /* ── Crystal bell tinkles — sparse, irregular ── */
    const bellMaster = ctx.createGain();
    bellMaster.gain.value = 1;
    bellMaster.connect(masterLpf);
    const bellNotes = [880, 1046.50, 1318.51, 1567.98, 1760, 2349.32];
    const bellTimer = setInterval(() => {
      if (Math.random() > 0.7) return;   // sparse trigger
      const ctxNow = ctx.currentTime;
      const f = bellNotes[Math.floor(Math.random() * bellNotes.length)];
      [f, f * 2, f * 3].forEach((freq, i) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = 'sine'; o.frequency.value = freq;
        g.gain.setValueAtTime(0, ctxNow);
        g.gain.linearRampToValueAtTime(0.025 / (i + 1), ctxNow + 0.05);
        g.gain.exponentialRampToValueAtTime(0.0001, ctxNow + 3.2);
        o.connect(g).connect(bellMaster);
        o.start(ctxNow); o.stop(ctxNow + 3.3);
      });
    }, 2400);

    /* ── Glass arpeggio — slow ascending triangle, stereo-panned ── */
    const arpOsc = ctx.createOscillator();
    const arpGain = ctx.createGain();
    const arpPan  = ctx.createStereoPanner();
    arpOsc.type = 'triangle';
    arpGain.gain.value = 0;
    arpOsc.connect(arpGain).connect(arpPan).connect(masterLpf);
    arpOsc.connect(arpGain).connect(arpPan).connect(tap2);  // extra echo on melody
    arpOsc.start(t);
    // Slow pan LFO
    const arpPanLfo = ctx.createOscillator();
    const arpPanLfoG = ctx.createGain();
    arpPanLfo.frequency.value = 0.07;
    arpPanLfoG.gain.value = 0.85;
    arpPanLfo.connect(arpPanLfoG).connect(arpPan.pan);
    arpPanLfo.start(t);
    const arpNotes = [523.25, 659.25, 783.99, 987.77, 783.99, 659.25];  // C5 E5 G5 B5 G5 E5
    let arpStep = 0;
    const arpTimer = setInterval(() => {
      const now = ctx.currentTime;
      arpOsc.frequency.setValueAtTime(arpNotes[arpStep % arpNotes.length], now);
      arpGain.gain.cancelScheduledValues(now);
      arpGain.gain.setValueAtTime(0, now);
      arpGain.gain.linearRampToValueAtTime(0.035, now + 0.25);
      arpGain.gain.exponentialRampToValueAtTime(0.0001, now + 2.0);
      arpStep += 1;
    }, 1600);

    droneRef.current = {
      master, breathLfo, subOsc, subLfo, chordVoices, chordTimer,
      bellTimer, arpOsc, arpPanLfo, arpTimer,
    };
  };

  const stopMelody = () => {
    const ctx = getCtx(); if (!ctx) return;
    const d = droneRef.current;
    if (!d) return;
    const t = ctx.currentTime;
    d.master.gain.cancelScheduledValues(t);
    d.master.gain.linearRampToValueAtTime(0.0001, t + 1.2);
    clearInterval(d.chordTimer);
    clearInterval(d.bellTimer);
    clearInterval(d.arpTimer);
    setTimeout(() => {
      try { d.breathLfo.stop(); } catch (_) {}
      try { d.subOsc.stop(); d.subLfo.stop(); } catch (_) {}
      try { d.chordVoices.forEach((v) => v.o.stop()); } catch (_) {}
      try { d.arpOsc.stop(); d.arpPanLfo.stop(); } catch (_) {}
      try { d.master.disconnect(); } catch (_) {}
      droneRef.current = null;
    }, 1300);
  };

  /* Brass bowl ring — for the gold beam finalise + lamp arrival. */
  const brass = () => {
    const ctx = getCtx(); if (!ctx) return;
    const t = ctx.currentTime;
    [220, 330, 440, 550].forEach((f, i) => {
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.type = i < 2 ? 'triangle' : 'sine'; o.frequency.value = f;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.linearRampToValueAtTime(0.15 / (i + 1), t + 0.18);   // softer attack, no click
      g.gain.exponentialRampToValueAtTime(0.0001, t + 2.6);
      o.connect(g).connect(ctx.destination); o.start(t); o.stop(t + 2.6);
    });
  };

  /* Smoke whisper — filtered pink-noise gust (genie emerge). */
  const whisper = (duration = 2.2) => {
    const ctx = getCtx(); if (!ctx) return;
    const t = ctx.currentTime;
    const bufSize = 2 * ctx.sampleRate;
    const noiseBuffer = ctx.createBuffer(1, bufSize, ctx.sampleRate);
    const out = noiseBuffer.getChannelData(0);
    for (let i = 0; i < bufSize; i++) out[i] = (Math.random() * 2 - 1) * 0.4;
    const src = ctx.createBufferSource(); src.buffer = noiseBuffer;
    const filt = ctx.createBiquadFilter(); filt.type = 'bandpass'; filt.frequency.value = 850; filt.Q.value = 1.2;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.22, t + 0.4);
    g.gain.linearRampToValueAtTime(0.0001, t + duration);
    src.connect(filt).connect(g).connect(ctx.destination); src.start(t); src.stop(t + duration);
  };

  /* Page rustle — short noise burst for parchment. */
  const rustle = () => {
    const ctx = getCtx(); if (!ctx) return;
    const t = ctx.currentTime;
    const buf = ctx.createBuffer(1, ctx.sampleRate * 0.6, ctx.sampleRate);
    const out = buf.getChannelData(0);
    for (let i = 0; i < buf.length; i++) out[i] = (Math.random() * 2 - 1) * (1 - i / buf.length);
    const src = ctx.createBufferSource(); src.buffer = buf;
    const filt = ctx.createBiquadFilter(); filt.type = 'highpass'; filt.frequency.value = 2400;
    const g = ctx.createGain();
    /* Envelope with soft attack + tail — no more click at start/end. */
    g.gain.setValueAtTime(0.0001, t);
    g.gain.linearRampToValueAtTime(0.12, t + 0.08);
    g.gain.linearRampToValueAtTime(0.0001, t + 0.6);
    src.connect(filt).connect(g).connect(ctx.destination); src.start(t); src.stop(t + 0.62);
  };

  /* Soft tick — when each new genie message appears. */
  const tick = () => chime(660, 0.45, 0.10);

  /* Wind whoosh — pilgrimage smoke travel. */
  const whoosh = () => {
    const ctx = getCtx(); if (!ctx) return;
    const t = ctx.currentTime;
    const buf = ctx.createBuffer(1, ctx.sampleRate * 1.8, ctx.sampleRate);
    const out = buf.getChannelData(0);
    for (let i = 0; i < buf.length; i++) out[i] = (Math.random() * 2 - 1) * 0.35;
    const src = ctx.createBufferSource(); src.buffer = buf;
    const filt = ctx.createBiquadFilter(); filt.type = 'lowpass';
    filt.frequency.setValueAtTime(400, t);
    filt.frequency.linearRampToValueAtTime(1800, t + 1.8);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.20, t + 0.6);
    g.gain.linearRampToValueAtTime(0.0001, t + 1.8);
    src.connect(filt).connect(g).connect(ctx.destination); src.start(t); src.stop(t + 1.8);
  };

  return { chime, swell, brass, whisper, rustle, tick, whoosh, startMelody, stopMelody };
};

/* ─────────────── Genie sigil avatar (used in dialogue) ─────────────── */

const GenieAvatar = ({ size = 32 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden
    style={{ filter: 'drop-shadow(0 0 8px rgba(120,160,255,0.55))' }}>
    <defs>
      <radialGradient id="geAvBody" cx="50%" cy="42%" r="55%">
        <stop offset="0%"  stopColor="#F2F8FF"/>
        <stop offset="38%" stopColor="#7FB1FF"/>
        <stop offset="100%" stopColor="#1E3A8A"/>
      </radialGradient>
      <linearGradient id="geAvTurban" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%"   stopColor="#FFF6D6"/>
        <stop offset="45%"  stopColor="#F1D27A"/>
        <stop offset="100%" stopColor="#7A5418"/>
      </linearGradient>
      <radialGradient id="geAvRuby" cx="40%" cy="35%" r="60%">
        <stop offset="0%"  stopColor="#FFD9D9"/>
        <stop offset="45%" stopColor="#E63946"/>
        <stop offset="100%" stopColor="#5A0A14"/>
      </radialGradient>
    </defs>
    {/* Turban (two wraps) */}
    <path d="M 8 19 Q 24 4, 40 19 Q 36 23, 24 23 Q 12 23, 8 19 Z"
      fill="url(#geAvTurban)" stroke="#5A4012" strokeWidth="0.5"/>
    <path d="M 13 14 Q 24 6, 35 14 Q 32 18, 24 18 Q 16 18, 13 14 Z"
      fill="url(#geAvTurban)" opacity="0.9" stroke="#5A4012" strokeWidth="0.35"/>
    {/* Ruby brooch */}
    <ellipse cx="24" cy="12" rx="2.6" ry="1.7" fill="url(#geAvRuby)" stroke="#5A4012" strokeWidth="0.3"/>
    {/* Plume */}
    <path d="M 24 9 Q 30 4, 35 6 Q 32 9, 28 10 Q 25 10, 24 9 Z"
      fill="url(#geAvTurban)" stroke="#5A4012" strokeWidth="0.3"/>
    {/* Head (humanoid silhouette) */}
    <path d="M 18 28 Q 18 24, 20 22 Q 24 21, 28 22 Q 30 24, 30 28
             Q 30 32, 27 34 Q 24 35, 21 34 Q 18 32, 18 28 Z"
      fill="url(#geAvBody)" opacity="0.95"/>
    {/* Neck */}
    <rect x="22" y="33" width="4" height="3" fill="url(#geAvBody)" opacity="0.9"/>
    {/* Shoulders / body wisp */}
    <path d="M 13 38 Q 24 42, 35 38 L 38 48 Q 24 46, 10 48 Z"
      fill="url(#geAvBody)" opacity="0.9"/>
  </svg>
);

/* ─────────────── Gold Aladdin lamp (premium SVG, 3D feel, jeweled) ─────────────── */

const GoldLamp = ({ size = 240 }) => (
  <svg width={size} height={size * 0.6} viewBox="0 0 400 240" aria-hidden
    style={{ filter: 'drop-shadow(0 28px 40px rgba(0,0,0,0.85)) drop-shadow(0 0 36px rgba(241,210,122,0.6))' }}>
    <defs>
      {/* Main bulbous body — vertical gold gradient with strong highlight */}
      <radialGradient id="lampBody" cx="38%" cy="32%" r="70%">
        <stop offset="0%"   stopColor="#FFF6D6"/>
        <stop offset="14%"  stopColor="#F5D97A"/>
        <stop offset="48%"  stopColor="#C99A3D"/>
        <stop offset="82%"  stopColor="#7A5418"/>
        <stop offset="100%" stopColor="#3A2410"/>
      </radialGradient>
      {/* Top cap (the dome with the knob) */}
      <linearGradient id="lampCap" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%"  stopColor="#FFF1B8"/>
        <stop offset="40%" stopColor="#F1D27A"/>
        <stop offset="100%" stopColor="#7A5418"/>
      </linearGradient>
      {/* Spout */}
      <linearGradient id="lampSpout" x1="0" y1="0" x2="1" y2="0.6">
        <stop offset="0%"   stopColor="#3A2410"/>
        <stop offset="35%"  stopColor="#8B6B1F"/>
        <stop offset="65%"  stopColor="#F1D27A"/>
        <stop offset="100%" stopColor="#FFF1B8"/>
      </linearGradient>
      {/* Handle */}
      <linearGradient id="lampHandle" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%"  stopColor="#FFF1B8"/>
        <stop offset="40%" stopColor="#C99A3D"/>
        <stop offset="100%" stopColor="#5A4012"/>
      </linearGradient>
      {/* Base */}
      <linearGradient id="lampBase" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%"   stopColor="#F1D27A"/>
        <stop offset="60%"  stopColor="#8B6B1F"/>
        <stop offset="100%" stopColor="#3A2410"/>
      </linearGradient>
      {/* Jewels */}
      <radialGradient id="rubyJ" cx="38%" cy="32%" r="60%">
        <stop offset="0%"  stopColor="#FFC4C4"/>
        <stop offset="35%" stopColor="#E63946"/>
        <stop offset="100%" stopColor="#5A0A14"/>
      </radialGradient>
      <radialGradient id="sapJ" cx="38%" cy="32%" r="60%">
        <stop offset="0%"  stopColor="#B8DCFF"/>
        <stop offset="35%" stopColor="#0F52BA"/>
        <stop offset="100%" stopColor="#06205A"/>
      </radialGradient>
      <radialGradient id="emeJ" cx="38%" cy="32%" r="60%">
        <stop offset="0%"  stopColor="#A8F0CC"/>
        <stop offset="35%" stopColor="#10B981"/>
        <stop offset="100%" stopColor="#063A26"/>
      </radialGradient>
      <radialGradient id="ambJ" cx="38%" cy="32%" r="60%">
        <stop offset="0%"  stopColor="#FFE6A0"/>
        <stop offset="35%" stopColor="#E89F32"/>
        <stop offset="100%" stopColor="#5A3A0A"/>
      </radialGradient>
    </defs>

    {/* Cast shadow under the lamp (soft) */}
    <ellipse cx="200" cy="226" rx="120" ry="8" fill="rgba(0,0,0,0.55)"/>

    {/* SPOUT — elongated cone on the left */}
    <path d="M 36 138
             L 110 118
             L 130 132
             L 110 150 Z"
      fill="url(#lampSpout)" stroke="#3A2410" strokeWidth="1"
      strokeLinejoin="round"/>
    {/* Spout opening highlight */}
    <ellipse cx="46" cy="139" rx="6" ry="11" fill="#1A0E04" stroke="#5A4012" strokeWidth="1"/>
    <ellipse cx="46" cy="138" rx="3" ry="7" fill="#000"/>
    {/* Spout collar ring (gold) */}
    <ellipse cx="106" cy="134" rx="6" ry="14" fill="url(#lampHandle)" stroke="#3A2410" strokeWidth="0.6"/>

    {/* MAIN BODY — bulbous teardrop */}
    <path d="M 100 140
             C 100 90, 160 78, 210 78
             C 290 78, 320 105, 320 142
             C 320 178, 290 198, 220 198
             C 150 198, 100 178, 100 140 Z"
      fill="url(#lampBody)" stroke="#3A2410" strokeWidth="1.2"/>

    {/* Engraved arabesque filigree — top of body */}
    <g opacity="0.55" stroke="#3A2410" fill="none" strokeLinecap="round">
      <path d="M 130 110 Q 200 88, 290 110" strokeWidth="0.9"/>
      <path d="M 134 116 Q 200 96, 286 116" strokeWidth="0.5"/>
      {[155, 180, 205, 230, 255].map((x, i) => (
        <g key={i}>
          <path d={`M ${x} 95 Q ${x + 6} 102, ${x + 12} 95`} strokeWidth="0.7"/>
          <path d={`M ${x + 3} 100 Q ${x + 6} 106, ${x + 9} 100`} strokeWidth="0.4"/>
        </g>
      ))}
    </g>

    {/* CENTRAL SAPPHIRE — flagship cabochon */}
    <ellipse cx="210" cy="138" rx="14" ry="9" fill="url(#sapJ)" stroke="#3A2410" strokeWidth="0.8"/>
    <ellipse cx="206" cy="135" rx="5" ry="2.5" fill="rgba(255,255,255,0.7)"/>
    {/* Gold prong setting around sapphire */}
    <g stroke="#3A2410" fill="url(#lampCap)" strokeWidth="0.5">
      <circle cx="196" cy="138" r="2.2"/>
      <circle cx="224" cy="138" r="2.2"/>
      <circle cx="210" cy="129" r="2"/>
      <circle cx="210" cy="147" r="2"/>
    </g>

    {/* Filigree band — engraved pattern around the middle */}
    <g opacity="0.55">
      <path d="M 116 152 C 160 158, 270 158, 308 152" stroke="#3A2410" strokeWidth="1.2" fill="none"/>
      <path d="M 116 158 C 160 164, 270 164, 308 158" stroke="#3A2410" strokeWidth="0.6" fill="none"/>
    </g>

    {/* Small jewel pearls along the band (alternating ruby, emerald, amber) */}
    {[
      { x: 140, c: 'url(#rubyJ)' }, { x: 165, c: 'url(#emeJ)' },
      { x: 190, c: 'url(#ambJ)' }, { x: 230, c: 'url(#ambJ)' },
      { x: 255, c: 'url(#emeJ)' }, { x: 280, c: 'url(#rubyJ)' },
    ].map((j, i) => (
      <g key={i}>
        <circle cx={j.x} cy="155" r="3.2" fill={j.c} stroke="#3A2410" strokeWidth="0.5"/>
        <circle cx={j.x - 0.8} cy="153.5" r="0.9" fill="rgba(255,255,255,0.85)"/>
      </g>
    ))}

    {/* Scalloped lower edge */}
    <g opacity="0.5" stroke="#3A2410" strokeWidth="0.7" fill="none">
      {[120, 140, 160, 180, 200, 220, 240, 260, 280, 300].map((x) => (
        <path key={x} d={`M ${x - 8} 175 Q ${x} 184, ${x + 8} 175`}/>
      ))}
    </g>

    {/* HIGHLIGHT — large soft sheen on the upper-left of the body */}
    <ellipse cx="155" cy="105" rx="40" ry="15" fill="rgba(255,255,255,0.5)" opacity="0.75"/>
    <ellipse cx="148" cy="102" rx="22" ry="6" fill="rgba(255,255,255,0.85)"/>
    {/* Small secondary highlight bottom-right */}
    <ellipse cx="275" cy="180" rx="20" ry="6" fill="rgba(255,255,255,0.25)"/>

    {/* TOP CAP — dome with knob */}
    <path d="M 180 78 Q 220 50, 260 78 Z"
      fill="url(#lampCap)" stroke="#3A2410" strokeWidth="1"/>
    {/* Cap collar */}
    <rect x="198" y="68" width="44" height="10" rx="2"
      fill="url(#lampCap)" stroke="#3A2410" strokeWidth="0.8"/>
    {/* Cap collar gem inlay (emerald pin) */}
    <ellipse cx="220" cy="73" rx="6" ry="2.4" fill="url(#emeJ)" stroke="#3A2410" strokeWidth="0.4"/>

    {/* Cap pinnacle bead — RUBY */}
    <circle cx="220" cy="46" r="7" fill="url(#rubyJ)" stroke="#3A2410" strokeWidth="0.7"/>
    <circle cx="218" cy="44" r="2.2" fill="#FFE6E6"/>
    {/* Tiny crown of pearls under the ruby */}
    <g stroke="#3A2410" strokeWidth="0.4">
      <circle cx="212" cy="54" r="1.6" fill="#FFF6D6"/>
      <circle cx="220" cy="56" r="1.6" fill="#FFF6D6"/>
      <circle cx="228" cy="54" r="1.6" fill="#FFF6D6"/>
    </g>

    {/* HANDLE — curled question-mark shape on the right */}
    <path d="M 300 130
             C 358 100, 388 160, 348 196
             C 326 218, 304 200, 304 186
             L 308 184
             C 312 196, 336 200, 348 178
             C 366 152, 340 122, 304 144 Z"
      fill="url(#lampHandle)" stroke="#3A2410" strokeWidth="1"
      strokeLinejoin="round"/>
    {/* Handle gem inlay */}
    <circle cx="362" cy="148" r="3" fill="url(#sapJ)" stroke="#3A2410" strokeWidth="0.4"/>

    {/* BASE — disc */}
    <ellipse cx="210" cy="200" rx="74" ry="11" fill="url(#lampBase)" stroke="#3A2410" strokeWidth="1"/>
    <ellipse cx="210" cy="200" rx="60" ry="6" fill="rgba(0,0,0,0.4)"/>
    <ellipse cx="210" cy="198" rx="60" ry="5" fill="url(#lampBase)"/>
    {/* Base jewel stud */}
    <circle cx="210" cy="200" r="2.6" fill="url(#rubyJ)" stroke="#3A2410" strokeWidth="0.3"/>
  </svg>
);

/* ─────────────── Smoke filter (SVG turbulence) ─────────────── */

const SmokeFilters = () => (
  <svg width="0" height="0" style={{ position: 'absolute', pointerEvents: 'none' }} aria-hidden>
    <defs>
      <filter id="smokeTurbulence" x="-50%" y="-50%" width="200%" height="200%">
        <feTurbulence type="fractalNoise" baseFrequency="0.012 0.024" numOctaves="2" seed="3">
          <animate attributeName="baseFrequency" dur="14s" values="0.012 0.024;0.018 0.032;0.012 0.024" repeatCount="indefinite"/>
        </feTurbulence>
        <feDisplacementMap in="SourceGraphic" scale="22"/>
        <feGaussianBlur stdDeviation="1.6"/>
      </filter>
      <filter id="smokeTurbulenceStrong" x="-50%" y="-50%" width="200%" height="200%">
        <feTurbulence type="fractalNoise" baseFrequency="0.018 0.036" numOctaves="3" seed="7">
          <animate attributeName="baseFrequency" dur="9s" values="0.018 0.036;0.026 0.05;0.018 0.036" repeatCount="indefinite"/>
        </feTurbulence>
        <feDisplacementMap in="SourceGraphic" scale="32"/>
        <feGaussianBlur stdDeviation="2.2"/>
      </filter>
    </defs>
  </svg>
);

/* ─────────────── Gold Page-Window Frame ───────────────
   Thin gold outline that hugs the container edges on all four sides.
   Bottom uses `var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px))` so it sits above any home
   indicator / gesture bar and is always fully visible.
   Purely visual — `pointer-events-none` so it can never block clicks. */
const ManifestGoldFrame = () => (
  <div className="absolute pointer-events-none z-[201]"
    style={{
      top: 'max(var(--safe-area-inset-top, env(safe-area-inset-top, 0px)), 4px)',
      left: 4, right: 4,
      bottom: 'max(var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)), 22px)',
      borderRadius: 22,
      border: `1.5px solid ${GOLD_LIGHT}`,
      boxShadow:
        `0 0 16px rgba(241,210,122,0.32), inset 0 0 22px rgba(241,210,122,0.10)`,
    }}
    aria-hidden>
    {/* Inner hairline — subtle double-frame look, both rounded */}
    <div className="absolute pointer-events-none"
      style={{
        inset: 4,
        borderRadius: 18,
        border: `1px solid rgba(245,230,160,0.28)`,
      }}/>
  </div>
);

/* ─────────────── Main component ─────────────── */

export const TheManifest = ({ onClose }) => {
  const { user } = useAuth() || {};
  const nickname = user?.nickname || user?.email?.split('@')[0] || 'Chosen One';
  const profilePic = user?.profile_picture;

  // Hardware/gesture back closes the tile (cancels any active speech first).
  useHistoryClose(true, useCallback(() => {
    try { window.speechSynthesis?.cancel(); } catch (_) {}
    onClose();
  }, [onClose]));

  /* phase machine */
  const [phase, setPhase] = useState('awakening');
  /* dialogue */
  const [step, setStep] = useState(0);          // 0..3 → which question is active
  const [messages, setMessages] = useState([]); // [{role, text}]
  const [input, setInput] = useState('');
  const audio = useAudio();
  const sealedScrollRef = useRef(null);
  const scrollListRef = useRef(null);

  /* ── PHASE 1: AWAKENING TIMELINE ─────────────────────────────────────── */
  /* The orb breathes through 8 colors then locks gold and fills the screen.
     Total runtime: ~6.4 s.  Each color breath = 0.65 s. */
  const [orbColorIndex, setOrbColorIndex] = useState(0);
  useEffect(() => {
    if (phase !== 'awakening') return;
    audio.swell(6.0);            // one long cinematic bloom — no tin-tin ring
    let i = 0;
    const interval = setInterval(() => {
      i += 1;
      if (i < CATEGORY_COLORS.length) {
        setOrbColorIndex(i);     // color still shifts, but no per-tick chime
      } else {
        clearInterval(interval);
      }
    }, 650);
    const finaliseTimer = setTimeout(() => {
      audio.brass();
      setPhase('lamp_appear');
    }, 6400);
    return () => { clearInterval(interval); clearTimeout(finaliseTimer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  /* ── PHASE 2 → 3: LAMP APPEAR then IDLE ─────────────────────────────── */
  useEffect(() => {
    if (phase !== 'lamp_appear') return;
    const t = setTimeout(() => setPhase('lamp_idle'), 2000);
    return () => clearTimeout(t);
  }, [phase]);

  /* ── PHASE 4: CLEAN SMOOTH SEQUENCE ─────────────────────────────────────
     1. CLICK LAMP   → smoke phase begins, smoke rises naturally and fills the page (2.4s)
     2. SMOKE COVERS → lamp dissolves into the smoke (genie_appear phase, 2s)
     3. LAMP HIDDEN  → genie smoothly rises through the smoke (still genie_appear)
     4. GENIE HOLD   → genie remains for ~2.5s
     5. GENIE HIDE   → genie melts into smoke (genie_vanish phase, 2.5s)
     6. CHAT OPENS   → dialogue phase begins.
     Each transition is paced 2-3 seconds for a smooth, deliberate ritual. */
  const tapLamp = () => {
    if (phase !== 'lamp_idle') return;
    audio.startMelody();
    audio.whisper(3.5);
    setPhase('smoke');                       // 0.0s — smoke begins rising
    // ── 2.5s in: lamp dissolves + genie rises (genie_appear) ──
    setTimeout(() => {
      setPhase('genie_appear');
      audio.whisper(2.4);
      audio.brass();
    }, 2500);
    // ── 6.5s in: genie melts back into smoke (genie_vanish) ──
    setTimeout(() => {
      setPhase('genie_vanish');
      audio.whisper(2.0);
    }, 6500);
    // ── 9.0s in: chat opens ──
    setTimeout(() => {
      setPhase('dialogue');
      setStep(0);
      setMessages([]);
      audio.tick();
    }, 9000);
  };

  /* ── PHASE 6: DIALOGUE — single Q-at-a-time, then reveal full script ─── */
  const submitAnswer = () => {
    const trimmed = input.trim();
    if (!trimmed) return;
    audio.tick();
    const userMsg = { role: 'user', text: trimmed };
    setInput('');
    // Append the question that was just asked + the user's answer.
    // We DO NOT also push the next question — it will be appended on its
    // own submit, so messages never duplicate.
    setMessages((m) => [...m, { role: 'genie', text: QUESTIONS[step] }, userMsg]);
    if (step < QUESTIONS.length - 1) {
      setStep((s) => s + 1);
    } else {
      audio.rustle();
      setTimeout(() => setPhase('script_reveal'), 700);
    }
  };

  /* ── PHASE 6b: SCRIPT REVEAL → SCRIPT DISSOLVE → PILGRIMAGE ──
     Strict sequential flow (no overlaps):
       1. script_reveal (5.0s): chat is fully visible for reading
       2. script_dissolve (1.6s): chat smoothly vanishes — nothing else
          appears on screen during this time
       3. pilgrimage: only AFTER the chat has fully vanished does the
          travel scene fade in, then the energy orb begins its journey. */
  useEffect(() => {
    if (phase !== 'script_reveal') return;
    audio.rustle();
    const t = setTimeout(() => {
      setPhase('script_dissolve');
    }, 5000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  useEffect(() => {
    if (phase !== 'script_dissolve') return;
    audio.whoosh();
    const t = setTimeout(() => {
      setPhase('pilgrimage');
    }, 1600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  /* ── PHASE 9: PILGRIMAGE → SEALED & SAVE ─────────────────────────────
     Pilgrimage scene timing (inside PilgrimagePath):
       0.0s  scene fades in (1.4s)
       0.2s→1.4s  gold path glow draws
       1.8s  energy orb begins (5.4s journey User → Genie → Divine)
       7.2s  orb arrives at Divine, lingers
     Seal fires at 8.4s — gives a 1.2s breath after arrival so the user
     clearly sees the orb reach the Divine waypoint before the parchment
     opens. */
  useEffect(() => {
    if (phase !== 'pilgrimage') return;
    const t = setTimeout(() => {
      audio.brass();
      setPhase('sealed');
      sealManifestation();
    }, 8400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  /* Persist the manifestation as a memory (category: manifest) so it lives
     in the user's gallery. Captures a small sealed-card SVG snapshot. */
  const sealManifestation = useCallback(async () => {
    try {
      if (!sealedScrollRef.current) return;
      const cv = await html2canvas(sealedScrollRef.current, {
        backgroundColor: '#04030a', scale: 2, useCORS: true, logging: false,
      });
      const blob = await new Promise((r) => cv.toBlob(r, 'image/png'));
      if (!blob) return;
      const file = new File([blob], `manifest_${Date.now()}.png`, { type: 'image/png' });
      const userMsgs = messages.filter((m) => m.role === 'user').map((m) => m.text);
      const fd = new FormData();
      fd.append('file', file);
      fd.append('category', 'manifest');
      fd.append('media_type', 'image');
      fd.append('source', 'manifest');
      fd.append('note', `Wish: ${userMsgs[0] || ''}`);
      fd.append('extra_data', JSON.stringify({
        v: 2, format: 'genie_lamp',
        nickname, sealedAt: new Date().toISOString(),
        wish:     userMsgs[0] || '',
        action:   userMsgs[1] || '',
        feeling:  userMsgs[2] || '',
        whoToTell:userMsgs[3] || '',
      }));
      await axios.post(`${BACKEND_URL}/api/memories`, fd);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Manifest seal failed:', e);
    }
  }, [messages, nickname]);

  /* Lock body scroll while modal is open + start continuous ambient melody. */
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    // Defer melody start until the first user interaction has happened —
    // browsers block AudioContext on auto-load. We try once and if it's
    // suspended we'll retry on the first lamp tap.
    audio.startMelody();
    return () => {
      document.body.style.overflow = '';
      audio.stopMelody();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const userAnswers = messages.filter((m) => m.role === 'user').map((m) => m.text);

  return (
    <div className="fixed inset-0 z-[200] overflow-hidden imperium-shell"
      style={{
        height: '100dvh',
        background: '#04030a',
      }}
      data-testid="manifest-modal">
      <SmokeFilters/>

      {/* Golden page-window outline that wraps every phase's visuals.
          Decorative only — never intercepts pointer events. */}
      <ManifestGoldFrame/>

      <style>{`
        @keyframes mfOrbBreathe {
          0%,100% { transform: translate(-50%,-50%) scale(0.55); opacity: 0.85; }
          50%     { transform: translate(-50%,-50%) scale(1.5);  opacity: 1; }
        }
        @keyframes mfRoyalWave {
          0%   { transform: translate(-50%,-50%) scale(0.35); opacity: 0.85; border-width: 3px; }
          70%  { opacity: 0.4; border-width: 1.5px; }
          100% { transform: translate(-50%,-50%) scale(5.5); opacity: 0; border-width: 0.5px; }
        }
        @keyframes mfBeamSweep {
          0%,100% { transform: translate(-50%,-50%) scale(0.75); opacity: 0.55; }
          50%     { transform: translate(-50%,-50%) scale(1.4);  opacity: 0.95; }
        }
        @keyframes mfIntentText {
          0%   { opacity: 0; letter-spacing: 0.02em; transform: translate(-50%, calc(-50% + 92px)) scale(0.85); }
          45%  { opacity: 1; letter-spacing: 0.18em; transform: translate(-50%, calc(-50% + 92px)) scale(1.02); }
          100% { opacity: 0.92; letter-spacing: 0.14em; transform: translate(-50%, calc(-50% + 92px)) scale(1); }
        }
        @keyframes mfDivineFill {
          0%   { transform: translate(-50%,-50%) scale(0.3);  opacity: 0; }
          40%  { transform: translate(-50%,-50%) scale(1.4);  opacity: 1; }
          100% { transform: translate(-50%,-50%) scale(4.2);  opacity: 0; }
        }
        @keyframes mfLampRise {
          0%   { transform: translate(-50%,12%) scale(0.55); opacity: 0; filter: brightness(2) blur(8px); }
          55%  { transform: translate(-50%,-50%) scale(1.05); opacity: 1; filter: brightness(1.15) blur(0px); }
          100% { transform: translate(-50%,-50%) scale(1.0);  opacity: 1; filter: brightness(1) blur(0px); }
        }
        @keyframes mfLampFloat {
          0%,100% { transform: translate(-50%,-50%) translateY(0); }
          50%     { transform: translate(-50%,-50%) translateY(-8px); }
        }
        @keyframes mfLampVanish {
          0%   { transform: translate(-50%,-50%) scale(1); opacity: 1; filter: blur(0); }
          100% { transform: translate(-50%,-50%) scale(0.7); opacity: 0; filter: blur(12px); }
        }
        @keyframes mfSmokeRise {
          0%   { transform: translate(-50%,0) scale(0.4); opacity: 0; }
          25%  { opacity: 0.95; }
          100% { transform: translate(-50%,-110%) scale(1.6); opacity: 0; }
        }
        @keyframes mfSmokeFromSpout {
          0%   { opacity: 0;    transform: scale(0.05, 0.1)  translateY(0); }
          12%  { opacity: 0.95; transform: scale(0.4, 0.5)   translateY(-30px); }
          40%  { opacity: 1;    transform: scale(0.9, 1.1)   translateY(-80px); }
          70%  { opacity: 0.95; transform: scale(1.2, 1.5)   translateY(-130px); }
          100% { opacity: 0.55; transform: scale(1.5, 1.8)   translateY(-170px); }
        }
        @keyframes mfSmokeFromSpoutSustain {
          0%,100% { opacity: 0.55; transform: scale(1.4, 1.7) translateY(-150px); }
          50%     { opacity: 0.75; transform: scale(1.5, 1.8) translateY(-160px); }
        }
        @keyframes mfGenieSmokeRise {
          0%   { opacity: 0; transform: translate(-50%, 0)    scale(0.6); }
          25%  { opacity: 0.85; }
          70%  { opacity: 0.5; }
          100% { opacity: 0; transform: translate(-50%, -180%) scale(1.4); }
        }
        @keyframes mfGenieAura {
          0%,100% { opacity: 0.55; transform: translate(-50%,0) scale(1.0); }
          50%     { opacity: 0.85; transform: translate(-50%,0) scale(1.08); }
        }
        @keyframes mfGenieRise {
          0%   { transform: translate(-50%,30%) scale(0.5); opacity: 0; filter: blur(14px); }
          45%  { transform: translate(-50%,-30%) scale(1.05); opacity: 0.9; filter: blur(2px); }
          100% { transform: translate(-50%,-50%) scale(1);   opacity: 1; filter: blur(0); }
        }
        @keyframes mfGenieHover {
          0%,100% { transform: translate(-50%,-50%) translateY(0); }
          50%     { transform: translate(-50%,-50%) translateY(-6px); }
        }
        @keyframes mfGenieVanish {
          0%   { transform: translate(-50%,-50%) scale(1)   translateY(0);    opacity: 1; filter: blur(0); }
          60%  { transform: translate(-50%,-50%) scale(0.85) translateY(-20px); opacity: 0.5; filter: blur(8px); }
          100% { transform: translate(-50%,-50%) scale(0.5)  translateY(-60px); opacity: 0; filter: blur(20px); }
        }
        @keyframes mfScrollUnroll {
          0%   { transform: translateY(40px) scaleY(0.05); opacity: 0; }
          60%  { transform: translateY(0) scaleY(0.5);    opacity: 1; }
          100% { transform: translateY(0) scaleY(1);      opacity: 1; }
        }
        @keyframes mfScrollDissolve {
          0%   { opacity: 1; filter: blur(0); }
          100% { opacity: 0; filter: blur(14px); transform: scale(0.6) rotate(8deg); }
        }
        @keyframes mfParticleFlight {
          0%   { opacity: 0; transform: translate(0, 0) scale(0.4); }
          15%  { opacity: 1; }
          50%  { transform: translate(var(--mid-x), var(--mid-y)) scale(1.4); }
          100% { opacity: 0; transform: translate(var(--end-x), var(--end-y)) scale(0.2); }
        }
        @keyframes mfPathDraw {
          0%   { opacity: 0; stroke-dashoffset: 1600; }
          100% { opacity: 1; stroke-dashoffset: 0; }
        }
        @keyframes mfPilgFadeIn {
          0%   { opacity: 0; transform: scale(1.04); filter: blur(8px); }
          40%  { opacity: 0.55; filter: blur(4px); }
          100% { opacity: 1; transform: scale(1); filter: blur(0); }
        }
        @keyframes mfWaypointArrive {
          0%   { box-shadow: 0 0 24px rgba(241,210,122,0.8), 0 0 48px rgba(241,210,122,0.4); }
          50%  { box-shadow: 0 0 48px rgba(255,246,214,1), 0 0 110px rgba(241,210,122,1), 0 0 180px rgba(255,255,255,0.8); transform: scale(1.18); }
          100% { box-shadow: 0 0 24px rgba(241,210,122,0.8), 0 0 48px rgba(241,210,122,0.4); transform: scale(1); }
        }
        @keyframes mfTravel {
          0%   { opacity: 0; offset-distance: 0%; }
          12%  { opacity: 1; }
          90%  { opacity: 1; }
          100% { opacity: 0; offset-distance: 100%; }
        }
        @keyframes mfTravelColor {
          0%   { fill: #FFF6D6; filter: drop-shadow(0 0 14px #FFF6D6) drop-shadow(0 0 28px #F1D27A); }
          20%  { fill: #DC143C; filter: drop-shadow(0 0 14px #DC143C) drop-shadow(0 0 28px #DC143C); }
          40%  { fill: #0F52BA; filter: drop-shadow(0 0 14px #0F52BA) drop-shadow(0 0 28px #0F52BA); }
          60%  { fill: #10B981; filter: drop-shadow(0 0 14px #10B981) drop-shadow(0 0 28px #10B981); }
          80%  { fill: #8B5CF6; filter: drop-shadow(0 0 14px #8B5CF6) drop-shadow(0 0 28px #8B5CF6); }
          100% { fill: #FFF6D6; filter: drop-shadow(0 0 18px #FFF6D6) drop-shadow(0 0 36px #F1D27A); }
        }
        @keyframes mfWaypointPulse {
          0%   { opacity: 0.95; transform: scale(0.8); border-width: 3px; }
          100% { opacity: 0;    transform: scale(2.2); border-width: 0.5px; }
        }
        @keyframes mfTwinkle {
          0%,100% { opacity: 0; transform: scale(0.4); }
          50%     { opacity: 1; transform: scale(1.2); }
        }
        @keyframes mfSealedTextIn {
          0%   { opacity: 0; transform: translate(-50%,-50%) scale(0.85); }
          100% { opacity: 1; transform: translate(-50%,-50%) scale(1.0);  }
        }
        @keyframes mfDivineBreathe {
          0%,100% { opacity: 0.7; transform: scale(0.85); }
          50%     { opacity: 1;   transform: scale(1.18); }
        }
        @keyframes mfScriptFadeOut {
          0%   { opacity: 1; filter: blur(0) brightness(1); }
          60%  { opacity: 0.6; filter: blur(4px) brightness(1.6); }
          100% { opacity: 0; filter: blur(16px) brightness(2.4) hue-rotate(20deg); }
        }
        @keyframes mfScriptVanishFast {
          0%   { opacity: 1;   filter: blur(0)    brightness(1);   }
          50%  { opacity: 0.55; filter: blur(8px) brightness(1.6); }
          100% { opacity: 0;   filter: blur(20px) brightness(2.6); }
        }
        @keyframes mfGoldenBeamFlash {
          0%   { opacity: 0; transform: scale(0.4); }
          50%  { opacity: 1; transform: scale(1.2); }
          100% { opacity: 0.85; transform: scale(1.0); }
        }
      `}</style>

      {/* Header + back buttons intentionally removed — once entered, the user
          must complete the ritual and tap Close at the end to return. */}
      {false && (
      <div
        className="absolute top-0 left-0 right-0 z-[260] flex items-center justify-between px-4 py-3"
        style={{
          paddingTop: 'calc(var(--safe-area-inset-top, env(safe-area-inset-top, 0px)) + 12px)',
          borderBottom: '1px solid rgba(212,175,55,0.18)',
          background: 'rgba(0,0,0,0.55)',
          backdropFilter: 'blur(8px)',
        }}
      >
        <BackPill onClick={() => { try { window.speechSynthesis?.cancel(); } catch (_) {} onClose(); }}
          accent="#FFC83D" testId="manifest-close"/>
        <div className="flex flex-col items-center pointer-events-none">
          <h2 className="font-['Cormorant_Garamond'] text-xl font-light tracking-wide"
            style={{ color: GOLD_LIGHT, textShadow: '0 0 14px rgba(241,210,122,0.45)' }}>
            The Manifest
          </h2>
          <p className="font-['Outfit'] uppercase tracking-[0.3em] mt-0.5"
            style={{ color: 'rgba(241,210,122,0.55)', fontSize: 9 }}>
            Genie's Wish
          </p>
        </div>
        <div className="w-6"/>
      </div>
      )}

      {/* No back button anywhere — close happens only via the "Close" pill
          at the end of the sealed phase, which fires onClose() and returns
          the user to the launcher. */}

      {/* ═══════════════════ PHASE 1: AWAKENING ═══════════════════ */}
      {phase === 'awakening' && (
        <div className="absolute inset-0" data-testid="manifest-awakening">
          {/* Royal expanding waves — three concentric rings that breathe
              outward (big wave) and dissolve, like throwing a pebble into
              a pool of light. */}
          {[0, 0.4, 0.8].map((delay, i) => (
            <div
              key={i}
              className="absolute pointer-events-none"
              style={{
                top: '50%', left: '50%',
                width: 80, height: 80,
                borderRadius: '50%',
                border: `2px solid ${CATEGORY_COLORS[orbColorIndex]}`,
                animation: `mfRoyalWave 2.4s ${delay}s ease-out infinite`,
                opacity: 0,
                mixBlendMode: 'screen',
                boxShadow: `0 0 24px ${CATEGORY_COLORS[orbColorIndex]}`,
                transition: 'border-color 0.6s, box-shadow 0.6s',
              }}
            />
          ))}
          {/* Soft halo behind the orb */}
          <div
            className="absolute"
            style={{
              top: '50%', left: '50%',
              width: 360, height: 360,
              animation: 'mfBeamSweep 2.6s ease-in-out infinite',
              background: `radial-gradient(circle,
                ${CATEGORY_COLORS[orbColorIndex]}88 0%,
                ${CATEGORY_COLORS[orbColorIndex]}33 28%,
                ${CATEGORY_COLORS[orbColorIndex]}11 55%,
                transparent 78%)`,
              filter: 'blur(14px)',
              mixBlendMode: 'screen',
              transition: 'background 0.6s ease-in-out',
            }}
          />
          {/* The orb itself — bigger royal breathing (0.6x → 1.4x) */}
          <div
            className="absolute"
            style={{
              top: '50%', left: '50%',
              width: 86, height: 86,
              borderRadius: '50%',
              background: `radial-gradient(circle at 38% 30%,
                #FFFFFF 0%,
                #FFF6D6 32%,
                ${CATEGORY_COLORS[orbColorIndex]}dd 65%,
                ${CATEGORY_COLORS[orbColorIndex]}55 100%)`,
              boxShadow: `0 0 44px ${CATEGORY_COLORS[orbColorIndex]},
                          0 0 96px ${CATEGORY_COLORS[orbColorIndex]}99,
                          inset 0 0 12px rgba(255,255,255,0.6)`,
              animation: 'mfOrbBreathe 2.4s ease-in-out infinite',
              mixBlendMode: 'screen',
              transition: 'background 0.6s, box-shadow 0.6s',
            }}
            data-testid="manifest-orb"
          />
          {/* "Let's manifest..." — the intent beam radiating from the orb */}
          <div className="absolute pointer-events-none"
            style={{
              top: '50%', left: '50%',
              transform: 'translate(-50%, calc(-50% + 92px))',
              animation: 'mfIntentText 3.0s ease-out 0.6s both',
            }}>
            <p className="font-['Cormorant_Garamond'] italic text-center whitespace-nowrap"
              style={{
                color: '#FFF6D6',
                fontSize: 22,
                letterSpacing: '0.14em',
                textShadow:
                  `0 0 14px ${CATEGORY_COLORS[orbColorIndex]}, ` +
                  '0 0 26px rgba(255,246,214,0.55), ' +
                  '0 0 42px rgba(241,210,122,0.35)',
                transition: 'text-shadow 0.6s ease-in-out',
              }}>
              Let&rsquo;s manifest&hellip;
            </p>
            <div className="mx-auto mt-2"
              style={{
                width: 120, height: 1.5,
                background: `linear-gradient(90deg, transparent, ${CATEGORY_COLORS[orbColorIndex]}, transparent)`,
                boxShadow: `0 0 10px ${CATEGORY_COLORS[orbColorIndex]}`,
                transition: 'background 0.6s, box-shadow 0.6s',
              }}/>
          </div>
        </div>
      )}

      {/* ═══════════════════ PHASE 2: DIVINE FILL + LAMP RISE ═══════════════════ */}
      {(phase === 'lamp_appear' || phase === 'lamp_idle' || phase === 'smoke' || phase === 'genie_appear' || phase === 'dialogue' || phase === 'scroll' || phase === 'pilgrimage' || phase === 'sealed') && (
        <>
          {/* The divine gold fill flash — fires only when entering lamp_appear */}
          {phase === 'lamp_appear' && (
            <div
              className="absolute pointer-events-none"
              style={{
                top: '50%', left: '50%',
                width: 600, height: 600,
                background: 'radial-gradient(circle, rgba(255,246,214,1) 0%, rgba(241,210,122,0.95) 30%, rgba(241,210,122,0.4) 60%, transparent 100%)',
                mixBlendMode: 'screen',
                animation: 'mfDivineFill 2.0s ease-out forwards',
              }}
            />
          )}
          {/* Persistent soft gold ambience — lasts through every phase after lamp */}
          <div className="absolute inset-0 pointer-events-none" style={{
            background: 'radial-gradient(ellipse at 50% 45%, rgba(241,210,122,0.18) 0%, rgba(241,210,122,0.06) 35%, transparent 70%)',
          }}/>
        </>
      )}

      {/* ═══════════════════ THE LAMP — POSITIONED AT BOTTOM ═══════════════════
           Lamp is visible ONLY during the lamp_appear/lamp_idle/smoke phases
           and the genie_appear phase (where it animates out). After that the
           lamp must STAY hidden — do NOT render it in genie_vanish/dialogue. */}
      {(phase === 'lamp_appear' || phase === 'lamp_idle' || phase === 'smoke' || phase === 'genie_appear') && (
        <button
          type="button"
          onClick={tapLamp}
          disabled={phase !== 'lamp_idle'}
          className="absolute z-[40]"
          style={{
            top: '74%', left: '50%',
            animation: phase === 'lamp_appear'
              ? 'mfLampRise 2.0s cubic-bezier(0.2,0.7,0.3,1) forwards'
              : phase === 'genie_appear'
                ? 'mfLampVanish 2.2s ease-in forwards'
                : 'mfLampFloat 4s ease-in-out infinite',
            cursor: phase === 'lamp_idle' ? 'pointer' : 'default',
            background: 'transparent', border: 'none',
            padding: 0,
          }}
          data-testid="manifest-lamp">
          <GoldLamp size={220}/>
        </button>
      )}

      {/* ═══════════════════ SMOKE — RISES VERTICALLY FROM SPOUT HOLE ═══════════════════ */}
      {(phase === 'smoke' || phase === 'genie_appear' || phase === 'genie_vanish') && (
        <div
          className="absolute inset-0 pointer-events-none z-[35]"
          data-testid="manifest-smoke">
          <div
            className="absolute"
            style={{
              /* Lamp sits at top 74%, the spout hole is to its left.
                 We anchor a smoke column at the spout tip and animate
                 it RISING UP, expanding as it fills the page. */
              top: '74%',
              left: 'calc(50% - 110px)',
              width: 12, height: 12,
              transformOrigin: 'center bottom',
              animation: phase === 'smoke'
                ? 'mfSmokeFromSpout 2.4s ease-out forwards'
                : 'mfSmokeFromSpoutSustain 4s ease-in-out infinite',
              opacity: 0,
            }}>
            <svg width="900" height="1400"
              viewBox="0 0 900 1400"
              style={{
                position: 'absolute',
                bottom: 0, left: '50%',
                transform: 'translateX(-50%)',
                filter: 'url(#smokeTurbulenceStrong)',
                overflow: 'visible',
              }}>
              <defs>
                <linearGradient id="smokeSpout" x1="0" y1="1" x2="0" y2="0">
                  <stop offset="0%"   stopColor="#E6F1FF" stopOpacity="0.9"/>
                  <stop offset="30%"  stopColor="#A6CCFF" stopOpacity="0.7"/>
                  <stop offset="65%"  stopColor="#4A75D8" stopOpacity="0.4"/>
                  <stop offset="100%" stopColor="#1E3A8A" stopOpacity="0"/>
                </linearGradient>
              </defs>
              {/* Tight column rising from the spout, blossoming upward */}
              <ellipse cx="450" cy="1280" rx="120" ry="60"  fill="url(#smokeSpout)"/>
              <ellipse cx="450" cy="1100" rx="200" ry="120" fill="url(#smokeSpout)"/>
              <ellipse cx="430" cy="900"  rx="260" ry="180" fill="url(#smokeSpout)"/>
              <ellipse cx="470" cy="700"  rx="320" ry="220" fill="url(#smokeSpout)"/>
              <ellipse cx="420" cy="500"  rx="360" ry="260" fill="url(#smokeSpout)"/>
              <ellipse cx="480" cy="300"  rx="400" ry="280" fill="url(#smokeSpout)"/>
              <ellipse cx="450" cy="120"  rx="420" ry="180" fill="url(#smokeSpout)"/>
            </svg>
          </div>
        </div>
      )}

      {/* ═══════════════════ GENIE PRESENCE (luminous silhouette) ═══════════════════ */}
      {(phase === 'genie_appear' || phase === 'genie_vanish' || phase === 'dialogue') && (
        <>
          {/* Continuous upward smoke trails — rise from above the genie's head
              like incense. Stops at ~40% so dialogue below stays untouched. */}
          {phase === 'dialogue' && (
            <div className="absolute pointer-events-none z-[37]"
              style={{
                top: 0, left: '50%',
                transform: 'translateX(-50%)',
                width: 280, height: '46%',
              }}>
              {[0, 1.6, 3.2].map((delay, i) => (
                <svg key={i} width="100" height="100%"
                  viewBox="0 0 100 400"
                  preserveAspectRatio="none"
                  style={{
                    position: 'absolute',
                    left: `${20 + i * 30}%`,
                    top: 0,
                    filter: 'url(#smokeTurbulence)',
                    animation: `mfGenieSmokeRise 5s ${delay}s ease-out infinite`,
                    opacity: 0,
                  }}>
                  <defs>
                    <radialGradient id={`gnSmkRise${i}`} cx="50%" cy="60%" r="50%">
                      <stop offset="0%"  stopColor="#E6F1FF" stopOpacity="0.7"/>
                      <stop offset="60%" stopColor="#7FB1FF" stopOpacity="0.35"/>
                      <stop offset="100%" stopColor="#1E3A8A" stopOpacity="0"/>
                    </radialGradient>
                  </defs>
                  <ellipse cx="50" cy="320" rx="35" ry="40" fill={`url(#gnSmkRise${i})`}/>
                  <ellipse cx="50" cy="220" rx="28" ry="44" fill={`url(#gnSmkRise${i})`}/>
                  <ellipse cx="50" cy="120" rx="20" ry="40" fill={`url(#gnSmkRise${i})`}/>
                </svg>
              ))}
            </div>
          )}
          {/* Continuous gentle smoke aura around the genie — only in the
              upper portion so the dialogue below stays clean and readable. */}
          {phase === 'dialogue' && (
            <div className="absolute pointer-events-none z-[36]"
              style={{
                top: '8%', left: '50%',
                transform: 'translate(-50%,0)',
                width: 320, height: 280,
                animation: 'mfGenieAura 7s ease-in-out infinite',
              }}>
              <svg width="100%" height="100%" viewBox="0 0 320 280"
                style={{ filter: 'url(#smokeTurbulence)' }}>
                <defs>
                  <radialGradient id="genieAuraSm" cx="50%" cy="50%" r="50%">
                    <stop offset="0%"  stopColor="#A6CCFF" stopOpacity="0.45"/>
                    <stop offset="55%" stopColor="#4A75D8" stopOpacity="0.22"/>
                    <stop offset="100%" stopColor="#1E3A8A" stopOpacity="0"/>
                  </radialGradient>
                </defs>
                <ellipse cx="160" cy="140" rx="140" ry="110" fill="url(#genieAuraSm)"/>
                <ellipse cx="100" cy="100" rx="60" ry="50"  fill="url(#genieAuraSm)"/>
                <ellipse cx="220" cy="120" rx="55" ry="48"  fill="url(#genieAuraSm)"/>
                <ellipse cx="160" cy="220" rx="100" ry="40" fill="url(#genieAuraSm)"/>
              </svg>
            </div>
          )}
          {/* Genie figure — TOP-RIGHT during dialogue, centred earlier. */}
          <div
            className="absolute pointer-events-none z-[38]"
            style={{
              top: phase === 'dialogue' ? 116 : '46%',
              right: phase === 'dialogue' ? 22 : 'auto',
              left: phase === 'dialogue' ? 'auto' : '50%',
              transform: phase === 'dialogue' ? 'none' : 'translate(-50%,-50%)',
              transition: 'top 1.0s ease-in-out, left 1.0s ease-in-out, right 1.0s ease-in-out, transform 1.0s ease-in-out',
              animation: phase === 'genie_appear'
                ? 'mfGenieRise 2.4s cubic-bezier(0.2,0.7,0.3,1) forwards'
                : phase === 'genie_vanish'
                  ? 'mfGenieVanish 2.4s ease-in forwards'
                  : 'mfGenieHover 4s ease-in-out infinite',
            }}
            data-testid="manifest-genie">
            <svg
              width={phase === 'dialogue' ? 110 : 240}
              height={phase === 'dialogue' ? 170 : 340}
              viewBox="0 0 220 340"
              style={{ filter: 'drop-shadow(0 0 32px rgba(127,177,255,0.65)) drop-shadow(0 0 78px rgba(241,210,122,0.4))' }}>
            <defs>
              <linearGradient id="genieSkin" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%"  stopColor="#F5FAFF" stopOpacity="0.94"/>
                <stop offset="35%" stopColor="#B5D6FF" stopOpacity="0.88"/>
                <stop offset="75%" stopColor="#5E86D8" stopOpacity="0.78"/>
                <stop offset="100%" stopColor="#2A4DA0" stopOpacity="0.4"/>
              </linearGradient>
              <linearGradient id="genieSkinSoft" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%"  stopColor="#B5D6FF" stopOpacity="0.8"/>
                <stop offset="100%" stopColor="#2A4DA0" stopOpacity="0.25"/>
              </linearGradient>
              <linearGradient id="genieBody" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%"  stopColor="#7FB1FF" stopOpacity="0.88"/>
                <stop offset="55%" stopColor="#3A5BC0" stopOpacity="0.72"/>
                <stop offset="100%" stopColor="#1E3A8A" stopOpacity="0.0"/>
              </linearGradient>
              <linearGradient id="genieTurban" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"  stopColor="#FFF6D6"/>
                <stop offset="35%" stopColor="#F1D27A"/>
                <stop offset="100%" stopColor="#7A5418"/>
              </linearGradient>
              <linearGradient id="genieVest" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"  stopColor="#F1D27A"/>
                <stop offset="100%" stopColor="#7A5418"/>
              </linearGradient>
              <radialGradient id="genieRuby" cx="38%" cy="32%" r="60%">
                <stop offset="0%"  stopColor="#FFD9D9"/>
                <stop offset="40%" stopColor="#E63946"/>
                <stop offset="100%" stopColor="#5A0A14"/>
              </radialGradient>
            </defs>

            {/* Soft gold aura halo behind the head */}
            <circle cx="110" cy="92" r="92" fill="rgba(241,210,122,0.12)"/>
            <circle cx="110" cy="92" r="66" fill="rgba(241,210,122,0.10)"/>

            {/* ── TURBAN — three-wrap with plume feather and ruby brooch ── */}
            <path d="M 36 92
                     Q 110 28, 184 92
                     Q 168 110, 110 110
                     Q 52 110, 36 92 Z"
              fill="url(#genieTurban)" stroke="#5A4012" strokeWidth="0.9"/>
            <path d="M 50 82
                     Q 110 38, 170 82
                     Q 154 96, 110 96
                     Q 66 96, 50 82 Z"
              fill="url(#genieTurban)" opacity="0.92" stroke="#5A4012" strokeWidth="0.6"/>
            <path d="M 64 70
                     Q 110 38, 156 70
                     Q 140 84, 110 84
                     Q 80 84, 64 70 Z"
              fill="url(#genieTurban)" stroke="#5A4012" strokeWidth="0.5"/>
            <g stroke="#5A4012" strokeWidth="0.4" fill="none" opacity="0.65">
              <path d="M 42 92 Q 110 70, 178 92"/>
              <path d="M 56 82 Q 110 60, 164 82"/>
              <path d="M 70 70 Q 110 52, 150 70"/>
            </g>
            <ellipse cx="110" cy="58" rx="9" ry="6" fill="url(#genieRuby)" stroke="#5A4012" strokeWidth="0.6"/>
            <ellipse cx="107" cy="56" rx="3" ry="1.6" fill="rgba(255,230,230,0.85)"/>
            <g stroke="#5A4012" fill="url(#genieTurban)" strokeWidth="0.4">
              <circle cx="100" cy="58" r="1.6"/><circle cx="120" cy="58" r="1.6"/>
              <circle cx="110" cy="52" r="1.6"/><circle cx="110" cy="64" r="1.6"/>
            </g>
            {/* Plume feather */}
            <path d="M 110 50
                     C 124 30, 142 22, 156 28
                     C 154 34, 148 40, 140 44
                     C 134 46, 124 48, 116 48
                     C 112 48, 110 49, 110 50 Z"
              fill="url(#genieTurban)" stroke="#5A4012" strokeWidth="0.5"/>
            <g stroke="#5A4012" strokeWidth="0.3" opacity="0.6">
              <path d="M 116 46 L 128 32" fill="none"/>
              <path d="M 120 47 L 136 34" fill="none"/>
              <path d="M 124 48 L 144 38" fill="none"/>
            </g>

            {/* ── HEAD — softer humanoid silhouette with gentler jaw curve ── */}
            <path d="M 84 108
                     C 84 100, 90 94, 110 92
                     C 130 94, 136 100, 136 108
                     C 136 124, 130 134, 122 138
                     C 116 142, 104 142, 98 138
                     C 90 134, 84 124, 84 108 Z"
              fill="url(#genieSkin)"/>
            {/* Soft cheek/jaw shadow — organic, not blocky */}
            <path d="M 90 122 C 100 130, 120 130, 130 122 C 126 138, 114 142, 110 142 C 106 142, 94 138, 90 122 Z"
              fill="rgba(42,77,160,0.28)" opacity="0.7"/>
            {/* Subtle brow line */}
            <path d="M 94 108 Q 110 102, 126 108" stroke="rgba(42,77,160,0.4)" strokeWidth="1" fill="none" strokeLinecap="round" opacity="0.7"/>
            {/* Tiny mythic goatee curl */}
            <path d="M 110 142 Q 113 152, 110 158 Q 107 152, 110 142 Z"
              fill="url(#genieSkinSoft)"/>

            {/* ── NECK — gently tapered, organic ── */}
            <path d="M 100 142 Q 100 152, 102 158 Q 110 161, 118 158 Q 120 152, 120 142 Q 110 145, 100 142 Z"
              fill="url(#genieSkin)" opacity="0.94"/>
            <ellipse cx="110" cy="147" rx="13" ry="2.5" fill="rgba(42,77,160,0.3)"/>

            {/* ── ROYAL SAPPHIRE NECKLACE — strung gold chain with central pendant ── */}
            <path d="M 70 158 Q 110 168, 150 158" stroke="url(#genieVest)" strokeWidth="1.2" fill="none"/>
            {/* Chain pearls */}
            {[80, 92, 104, 116, 128, 140].map((cx, i) => (
              <circle key={i} cx={cx} cy={161 + Math.abs(cx - 110) * 0.06} r="1.4"
                fill="url(#genieVest)" stroke="#5A4012" strokeWidth="0.3"/>
            ))}
            {/* Sapphire pendant in centre */}
            <path d="M 105 168 L 115 168 L 117 172 L 110 180 L 103 172 Z"
              fill="#0F52BA" stroke="#5A4012" strokeWidth="0.5"/>
            <path d="M 105 168 L 115 168 L 110 174 Z" fill="rgba(255,255,255,0.6)"/>

            {/* ── SHOULDERS + UPPER TORSO — soft rounded silhouette ── */}
            <path d="M 56 168
                     C 64 156, 82 152, 102 156
                     L 110 158
                     L 118 156
                     C 138 152, 156 156, 164 168
                     C 170 180, 172 196, 168 210
                     C 156 218, 130 220, 110 220
                     C 90 220, 64 218, 52 210
                     C 48 196, 50 180, 56 168 Z"
              fill="url(#genieSkin)"/>
            {/* Arm hints — flowing curves, not segmented */}
            <path d="M 52 184 Q 44 200, 50 218" stroke="rgba(42,77,160,0.42)" strokeWidth="3" fill="none" strokeLinecap="round"/>
            <path d="M 168 184 Q 176 200, 170 218" stroke="rgba(42,77,160,0.42)" strokeWidth="3" fill="none" strokeLinecap="round"/>

            {/* ── ETHEREAL GOLD VEST (soft draped, not geometric) ── */}
            <path d="M 78 162
                     C 84 174, 92 180, 100 184
                     L 100 222
                     C 90 224, 80 220, 76 214
                     C 72 200, 73 178, 78 162 Z"
              fill="url(#genieVest)" stroke="#5A4012" strokeWidth="0.6" opacity="0.95"/>
            <path d="M 142 162
                     C 136 174, 128 180, 120 184
                     L 120 222
                     C 130 224, 140 220, 144 214
                     C 148 200, 147 178, 142 162 Z"
              fill="url(#genieVest)" stroke="#5A4012" strokeWidth="0.6" opacity="0.95"/>
            {/* Vest embroidered curving lines */}
            <g stroke="#5A4012" strokeWidth="0.4" fill="none" opacity="0.7">
              <path d="M 82 178 Q 90 184, 98 182"/>
              <path d="M 82 196 Q 90 202, 98 200"/>
              <path d="M 138 178 Q 130 184, 122 182"/>
              <path d="M 138 196 Q 130 202, 122 200"/>
            </g>
            {/* Small gem studs */}
            <circle cx="92" cy="184" r="1.8" fill="url(#genieRuby)" stroke="#5A4012" strokeWidth="0.3"/>
            <circle cx="92" cy="204" r="1.8" fill="url(#genieRuby)" stroke="#5A4012" strokeWidth="0.3"/>
            <circle cx="128" cy="184" r="1.8" fill="url(#genieRuby)" stroke="#5A4012" strokeWidth="0.3"/>
            <circle cx="128" cy="204" r="1.8" fill="url(#genieRuby)" stroke="#5A4012" strokeWidth="0.3"/>

            {/* Gold sash with flowing drape */}
            <path d="M 64 216 Q 110 224, 156 216 L 158 228 Q 110 234, 62 228 Z"
              fill="url(#genieVest)" stroke="#5A4012" strokeWidth="0.6"/>
            <path d="M 64 216 Q 110 220, 156 216" stroke="rgba(255,255,255,0.45)" strokeWidth="1.4" fill="none"/>
            {/* Sash buckle — emerald centerpiece in gold setting */}
            <ellipse cx="110" cy="222" rx="9" ry="4.5" fill="url(#genieVest)" stroke="#5A4012" strokeWidth="0.6"/>
            <ellipse cx="110" cy="222" rx="6" ry="3"
              fill="#10B981" stroke="#063A26" strokeWidth="0.4"/>
            <ellipse cx="108" cy="220.5" rx="2.2" ry="1.2" fill="rgba(220,255,235,0.7)"/>

            {/* ── LOWER BODY — gracefully fades into smoke wisps ── */}
            <path d="M 54 228
                     Q 110 240, 166 228
                     L 172 280
                     Q 110 290, 48 280 Z"
              fill="url(#genieBody)" opacity="0.92"/>
            <path d="M 60 280
                     Q 92 320, 88 340
                     L 132 340
                     Q 128 320, 160 280
                     Q 110 292, 60 280 Z"
              fill="url(#genieBody)" opacity="0.55"
              style={{ filter: 'url(#smokeTurbulence)' }}/>
            <ellipse cx="110" cy="334" rx="48" ry="7"
              fill="url(#genieBody)" opacity="0.35"
              style={{ filter: 'url(#smokeTurbulenceStrong)' }}/>
          </svg>
        </div>
        </>
      )}

      {/* ═══════════════════ PHASE 6: DIALOGUE ═══════════════════
          Question TL · Genie TR (fixed) ·
          User-avatar BL (small, fixed) · User typing-text right of avatar.
          Compact layout that stays fully visible above on-screen keyboard. */}
      {phase === 'dialogue' && (
        <div className="absolute inset-0 z-[50]"
          data-testid="manifest-dialogue">

          {/* Question — LEFT (mirrors the genie at top-right) */}
          <div className="absolute pointer-events-none"
            style={{ top: 110, left: 18, right: 160 }}>
            <motion.p
              key={`q-${step}`}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.2, 0.7, 0.3, 1] }}
              className="font-['Cormorant_Garamond'] italic"
              style={{
                color: '#E6F1FF',
                fontSize: 19, lineHeight: 1.4,
                textShadow: '0 0 14px rgba(127,177,255,0.6)',
              }}
              data-testid="manifest-current-question">
              "{QUESTIONS[step]}"
            </motion.p>
            <p className="font-['Outfit'] uppercase tracking-[0.3em] mt-2"
              style={{ color: 'rgba(127,177,255,0.6)', fontSize: 9 }}>
              the genie asks · {step + 1} / {QUESTIONS.length}
            </p>
          </div>

          {/* User avatar — BL, smaller circle (64px). Fixed pixel position
              so the keyboard never pushes it off-screen. */}
          <div className="absolute"
            style={{ top: 230, left: 18 }}>
            <div className="w-[64px] h-[64px] rounded-full overflow-hidden"
              style={{
                border: `2px solid ${GOLD}`,
                boxShadow: '0 0 18px rgba(241,210,122,0.55), 0 0 36px rgba(241,210,122,0.28)',
                background: '#0a0610',
              }}>
              {profilePic ? (
                <img src={profilePic} alt={nickname} className="w-full h-full object-cover" draggable={false}/>
              ) : (
                <div className="w-full h-full flex items-center justify-center font-['Cormorant_Garamond'] italic"
                  style={{ color: GOLD_LIGHT, fontSize: 28 }}>
                  {(nickname || 'A')[0].toUpperCase()}
                </div>
              )}
            </div>
            <p className="font-['Outfit'] uppercase tracking-[0.25em] mt-1 text-center truncate"
              style={{ color: GOLD_LIGHT, fontSize: 8, maxWidth: 64 }}>{nickname}</p>
          </div>

          {/* User typing area — to the RIGHT of the small avatar. */}
          <div className="absolute"
            style={{ top: 232, left: 96, right: 18 }}>
            <div key={`a-${step}`}>
              <input
                type="text"
                inputMode="text"
                enterKeyHint="send"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitAnswer(); }
                }}
                autoFocus
                placeholder={step === 0 ? 'Speak your wish…' : 'Answer the genie…'}
                className="w-full font-['Cormorant_Garamond'] italic"
                style={{
                  background: 'transparent',
                  border: 'none',
                  outline: 'none',
                  color: GOLD_LIGHT,
                  fontSize: 18,
                  lineHeight: 1.4,
                  padding: 0,
                  caretColor: GOLD_LIGHT,
                  textShadow: '0 0 12px rgba(241,210,122,0.5)',
                  height: 64,
                  fontStyle: 'italic',
                }}
                data-testid="manifest-input"
              />
              <button
                onClick={submitAnswer}
                disabled={!input.trim()}
                className="mt-2 px-4 py-1.5 rounded-full font-['Outfit'] uppercase tracking-[0.35em] disabled:opacity-30"
                style={{
                  color: GOLD_LIGHT, fontSize: 10,
                  background: 'linear-gradient(135deg, rgba(241,210,122,0.16), rgba(122,84,24,0.10))',
                  border: '1.5px solid rgba(241,210,122,0.65)',
                  boxShadow: '0 0 14px rgba(241,210,122,0.4), inset 0 0 6px rgba(241,210,122,0.18)',
                  textShadow: '0 0 8px rgba(241,210,122,0.55)',
                }}
                data-testid="manifest-submit">
                Send · {step + 1} / {QUESTIONS.length}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════ PHASE 6b: SCRIPT REVEAL & DISSOLVE ═══════════════════
          During script_reveal the chat history is fully visible for reading.
          During script_dissolve the chat smoothly vanishes (nothing else is
          rendered on screen). Only after dissolve finishes does pilgrimage
          render. This guarantees a clean discrete handoff. */}
      {(phase === 'script_reveal' || phase === 'script_dissolve') && (
        <div className="absolute inset-0 z-[80] flex flex-col px-4"
          style={{
            paddingTop: 60, paddingBottom: 30,
            /* script_reveal: fully visible, no animation.
               script_dissolve: smoothly fade-and-blur to 0 over 1.4s
               (the phase holds 1.6s so the dissolve completes fully
               before pilgrimage renders).  */
            animation: phase === 'script_dissolve'
              ? 'mfScriptVanishFast 1.4s ease-in forwards'
              : 'none',
            pointerEvents: 'none',
          }}
          data-testid="manifest-script-reveal">
          <motion.p
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="font-['Cormorant_Garamond'] italic text-center mb-5"
            style={{ color: GOLD_LIGHT, fontSize: 16,
                     textShadow: '0 0 14px rgba(241,210,122,0.65)' }}>
            ✦  The Sacred Dialogue  ✦
          </motion.p>
          <div className="flex-1 overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
            {messages.map((m, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, x: m.role === 'genie' ? -14 : 14 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.35 + i * 0.25, duration: 0.55 }}>
                <DialogueRow role={m.role} text={m.text} profilePic={profilePic} nickname={nickname}/>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* ═══════════════════ PHASE 8: PILGRIMAGE — DESTINATION PATH ═══════════════════ */}
      {phase === 'pilgrimage' && (
        <PilgrimagePath profilePic={profilePic} nickname={nickname}/>
      )}

      {/* ═══════════════════ PHASE 9: SEALED — GOLDEN SCRIPT ═══════════════════
          A golden beam emerges from centre, opens into an ornate golden
          script/parchment, and all three voices (Divine top, User left,
          Genie right) live INSIDE the script. */}
      {phase === 'sealed' && (
        <div className="absolute inset-0 z-[100] flex items-center justify-center px-3 py-4 overflow-y-auto"
          data-testid="manifest-sealed">
          {/* Golden beam emerging from centre — first second */}
          <div className="absolute inset-0 pointer-events-none"
            style={{
              background: 'radial-gradient(circle at 50% 45%, rgba(255,246,214,0.95) 0%, rgba(241,210,122,0.55) 18%, rgba(241,210,122,0.15) 45%, rgba(0,0,0,0) 70%)',
              animation: 'mfGoldenBeamFlash 2.4s ease-out forwards',
            }}/>
          {/* The Golden Script — ornate gold-bordered parchment that
              "opens" from centre, expanding to its full size. */}
          <motion.div
            initial={{ scale: 0.0, opacity: 0, rotateX: 70 }}
            animate={{ scale: 1, opacity: 1, rotateX: 0 }}
            transition={{ duration: 1.4, delay: 1.0, ease: [0.2, 0.7, 0.3, 1] }}
            className="relative w-full"
            style={{
              maxWidth: 420,
              maxHeight: '92dvh',
              transformOrigin: 'center center',
            }}
            data-testid="manifest-golden-script">
            {/* Outer gold frame */}
            <div className="relative"
              style={{
                padding: '14px 12px',
                background:
                  'linear-gradient(135deg, #FFF6D6 0%, #F1D27A 30%, #C99A3D 65%, #7A5418 100%)',
                borderRadius: 14,
                boxShadow:
                  '0 0 32px rgba(241,210,122,0.6),' +
                  '0 0 60px rgba(241,210,122,0.35),' +
                  'inset 0 0 0 1px rgba(255,255,255,0.35)',
              }}>
              {/* Inner parchment */}
              <div className="relative"
                style={{
                  padding: '20px 16px 22px',
                  borderRadius: 8,
                  background:
                    'radial-gradient(ellipse at 30% 0%, rgba(255,255,255,0.45), transparent 55%),' +
                    'radial-gradient(ellipse at 70% 100%, rgba(120,80,20,0.18), transparent 55%),' +
                    'linear-gradient(180deg, #FBEFCB 0%, #F2DDB1 50%, #E0C58B 100%)',
                  boxShadow: 'inset 0 0 0 1px rgba(140,90,30,0.4)',
                }}>
                {/* Faint horizontal rule pattern */}
                <div className="absolute inset-0 pointer-events-none" style={{
                  background: 'repeating-linear-gradient(180deg, rgba(120,80,20,0.05) 0px, rgba(120,80,20,0.05) 1px, transparent 1px, transparent 28px)',
                }}/>

                {/* DIVINE — TOP */}
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 2.0, duration: 1.0 }}
                  className="relative text-center">
                  <div className="mx-auto" style={{
                    width: 60, height: 60,
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(255,255,255,1) 0%, rgba(255,246,214,0.85) 30%, rgba(241,210,122,0.4) 65%, transparent 100%)',
                    filter: 'blur(1px)',
                    animation: 'mfDivineBreathe 2.6s ease-in-out infinite',
                  }}/>
                  <p className="font-['Cormorant_Garamond'] italic mt-1"
                    style={{ color: '#3A2410', fontSize: 16, lineHeight: 1.45 }}>
                    "Take a first step firmly.<br/>
                    No need to see the full staircase —<br/>
                    just take the first step."
                  </p>
                  <p className="font-['Outfit'] uppercase tracking-[0.32em] mt-1.5"
                    style={{ color: '#7A5418', fontSize: 8.5 }}>
                    — the divine whispers
                  </p>
                </motion.div>

                {/* Decorative gold flourish */}
                <div className="my-4 flex items-center justify-center gap-2">
                  <div style={{ height: 1, flex: 1, background: 'linear-gradient(90deg, transparent, #B7892D, transparent)' }}/>
                  <span style={{ color: '#B7892D', fontSize: 10 }}>✦</span>
                  <div style={{ height: 1, flex: 1, background: 'linear-gradient(90deg, transparent, #B7892D, transparent)' }}/>
                </div>

                {/* USER LEFT · GENIE RIGHT */}
                <div className="grid grid-cols-2 gap-3 mt-1">
                  <motion.div
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 2.6, duration: 0.9 }}
                    data-testid="manifest-sealed-user">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0"
                        style={{ border: '1.5px solid #B7892D', boxShadow: '0 0 8px rgba(183,137,45,0.5)' }}>
                        {profilePic ? (
                          <img src={profilePic} alt={nickname} className="w-full h-full object-cover"/>
                        ) : (
                          <div className="w-full h-full flex items-center justify-center font-['Cormorant_Garamond'] italic"
                            style={{ color: '#FBEFCB', fontSize: 14, background: '#3A2410' }}>
                            {(nickname || 'A')[0].toUpperCase()}
                          </div>
                        )}
                      </div>
                      <span className="font-['Outfit'] uppercase tracking-[0.22em] truncate"
                        style={{ color: '#7A5418', fontSize: 8 }}>{nickname}</span>
                    </div>
                    {['wish','what you would do','how you would feel','who you would tell'].map((lbl, i) => (
                      <div key={i} className="mb-1.5">
                        <p className="font-['Outfit'] uppercase tracking-[0.25em]"
                          style={{ color: 'rgba(122,84,24,0.7)', fontSize: 7 }}>{lbl}</p>
                        <p className="font-['Cormorant_Garamond'] italic"
                          style={{ color: '#3A2410', fontSize: 12.5, lineHeight: 1.35 }}>
                          "{userAnswers[i] || '—'}"
                        </p>
                      </div>
                    ))}
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 3.0, duration: 0.9 }}
                    className="text-right"
                    data-testid="manifest-sealed-genie">
                    <div className="flex items-center gap-2 mb-2 justify-end">
                      <span className="font-['Outfit'] uppercase tracking-[0.22em]"
                        style={{ color: '#7A5418', fontSize: 8 }}>The Genie</span>
                      <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center"
                        style={{ border: '1.5px solid #B7892D', boxShadow: '0 0 8px rgba(183,137,45,0.5)', background: '#3A2410' }}>
                        <GenieAvatar size={26}/>
                      </div>
                    </div>
                    {['hears','sees','blesses','seals'].map((lbl, i) => {
                      const lines = [
                        'Your wish is heard.',
                        'The path is laid.',
                        'Walk forward.',
                        'It is sealed.',
                      ];
                      return (
                        <div key={i} className="mb-1.5">
                          <p className="font-['Outfit'] uppercase tracking-[0.25em]"
                            style={{ color: 'rgba(122,84,24,0.7)', fontSize: 7 }}>{lbl}</p>
                          <p className="font-['Cormorant_Garamond'] italic"
                            style={{ color: '#3A2410', fontSize: 12.5, lineHeight: 1.35 }}>
                            "{lines[i]}"
                          </p>
                        </div>
                      );
                    })}
                  </motion.div>
                </div>

                <p className="font-['Cormorant_Garamond'] italic text-center mt-3"
                  style={{ color: '#7A5418', fontSize: 12 }}>
                  ~ sealed in your gallery ~
                </p>
              </div>
            </div>
          </motion.div>

          {/* Close button — anchored bottom-centre, outside the script */}
          <motion.button
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ delay: 3.6 }}
            onClick={onClose}
            className="absolute left-1/2 -translate-x-1/2 px-6 py-2 rounded-full font-['Outfit'] uppercase tracking-[0.3em]"
            style={{
              bottom: 'calc(48px + var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)))',
              background: `linear-gradient(135deg, ${GOLD_LIGHT}, ${GOLD_DARK})`,
              color: '#1a0e04', fontSize: 10,
              boxShadow: '0 0 22px rgba(241,210,122,0.55)',
            }}
            data-testid="manifest-sealed-close">
            Close
          </motion.button>
        </div>
      )}

      {/* ── Off-screen sealed-card capture target (used by html2canvas) ── */}
      <div ref={sealedScrollRef}
        style={{
          position: 'fixed', left: -3000, top: 0,
          width: 540, padding: 36,
          background: 'radial-gradient(ellipse at center, #1a0d04 0%, #04030a 70%, #000 100%)',
          fontFamily: "'Cormorant Garamond', serif",
        }}>
        <p style={{ textAlign: 'center', color: GOLD_LIGHT, fontSize: 26, fontStyle: 'italic', margin: 0, textShadow: '0 0 18px rgba(241,210,122,0.6)' }}>
          The Manifest
        </p>
        <p style={{ textAlign: 'center', color: 'rgba(241,210,122,0.55)', fontSize: 11, letterSpacing: '0.3em', textTransform: 'uppercase', marginTop: 6 }}>
          {nickname} · genie's wish
        </p>
        <div style={{ marginTop: 28 }}>
          <SealedScroll answers={userAnswers} nickname={nickname} compact/>
        </div>
        <p style={{ textAlign: 'center', color: GOLD_LIGHT, fontStyle: 'italic', fontSize: 14, marginTop: 28, opacity: 0.7 }}>
          Sealed on {new Date().toLocaleDateString()}
        </p>
      </div>
    </div>
  );
};

/* ─────────────── Dialogue row (avatar + text bubble) ─────────────── */

const DialogueRow = ({ role, text, profilePic, nickname }) => {
  const isGenie = role === 'genie';
  return (
    <motion.div
      initial={{ opacity: 0, x: isGenie ? -16 : 16, y: 8 }}
      animate={{ opacity: 1, x: 0, y: 0 }}
      transition={{ duration: 0.55, ease: [0.2, 0.7, 0.3, 1] }}
      className="flex items-start gap-2 mb-3"
      data-testid={`manifest-msg-${role}`}>
      {/* Avatar — genie OR user — always at the LEFT of the row */}
      <div className="flex-shrink-0 w-9 h-9 rounded-full overflow-hidden"
        style={{
          border: `1.5px solid ${isGenie ? '#7FB1FF' : GOLD}`,
          boxShadow: `0 0 12px ${isGenie ? 'rgba(127,177,255,0.5)' : 'rgba(212,175,55,0.5)'}`,
          background: '#0a0610',
        }}>
        {isGenie ? (
          <div className="w-full h-full flex items-center justify-center">
            <GenieAvatar size={32}/>
          </div>
        ) : profilePic ? (
          <img src={profilePic} alt={nickname}
            className="w-full h-full object-cover" draggable={false}/>
        ) : (
          <div className="w-full h-full flex items-center justify-center font-['Cormorant_Garamond'] italic"
            style={{ color: GOLD_LIGHT, fontSize: 16, background: 'linear-gradient(135deg, #1a0d04, #2a1d0a)' }}>
            {(nickname || 'A')[0].toUpperCase()}
          </div>
        )}
      </div>
      {/* Bubble */}
      <div
        className="px-3 py-2 rounded-2xl max-w-[78%] font-['Cormorant_Garamond']"
        style={{
          background: isGenie
            ? 'linear-gradient(135deg, rgba(127,177,255,0.14), rgba(30,58,138,0.18))'
            : 'linear-gradient(135deg, rgba(241,210,122,0.16), rgba(122,84,24,0.18))',
          border: `1px solid ${isGenie ? 'rgba(127,177,255,0.4)' : 'rgba(241,210,122,0.4)'}`,
          color: isGenie ? '#E6F1FF' : GOLD_LIGHT,
          fontStyle: 'italic',
          fontSize: 16,
          lineHeight: 1.5,
          wordBreak: 'break-word',
          overflowWrap: 'anywhere',
          whiteSpace: 'pre-wrap',
          boxShadow: `0 0 14px ${isGenie ? 'rgba(127,177,255,0.18)' : 'rgba(241,210,122,0.15)'}`,
        }}>
        {text}
      </div>
    </motion.div>
  );
};

/* ─────────────── Sealed parchment scroll (with the 4 answers) ─────────────── */

const SealedScroll = ({ answers, nickname, compact = false }) => {
  const labels = ['Your wish', 'What you would do', 'How you would feel', 'Who you would tell'];
  return (
    <div className="relative" style={{ padding: '14px 0' }}>
      {/* Top gold rod */}
      <div style={{
        height: 18,
        background: 'linear-gradient(180deg, #F1D27A 0%, #C99A3D 50%, #7A5A18 100%)',
        borderRadius: '6px 6px 2px 2px',
        boxShadow: '0 3px 6px rgba(0,0,0,0.4)',
        position: 'relative',
      }}>
        <div style={{ position: 'absolute', left: -7, top: -3, width: 16, height: 24, borderRadius: '50%',
          background: 'radial-gradient(circle at 35% 30%, #F1D27A, #B7892D 60%, #5a3f0a)',
          boxShadow: '0 2px 5px rgba(0,0,0,0.45)' }}/>
        <div style={{ position: 'absolute', right: -7, top: -3, width: 16, height: 24, borderRadius: '50%',
          background: 'radial-gradient(circle at 35% 30%, #F1D27A, #B7892D 60%, #5a3f0a)',
          boxShadow: '0 2px 5px rgba(0,0,0,0.45)' }}/>
      </div>
      {/* Parchment body */}
      <div className="relative" style={{
        background: `
          radial-gradient(ellipse at 30% 0%, rgba(255,255,255,0.5), transparent 50%),
          radial-gradient(circle at 80% 90%, rgba(140,90,30,0.18), transparent 50%),
          linear-gradient(180deg, #FAEFCB 0%, #F2DDB1 50%, #E0C58B 100%)`,
        boxShadow: 'inset 0 0 0 2px rgba(140,90,30,0.45), inset 0 0 80px rgba(140,90,30,0.2), 0 0 28px rgba(241,210,122,0.35)',
        padding: compact ? '18px 24px' : '20px 22px',
      }}>
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: 'repeating-linear-gradient(180deg, rgba(120,80,20,0.06) 0px, rgba(120,80,20,0.06) 1px, transparent 1px, transparent 28px)',
        }}/>
        <p style={{
          fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic',
          textAlign: 'center', color: '#5a3f0a', fontSize: 16, marginBottom: 14,
        }}>Dearest {nickname},</p>
        {labels.map((lbl, i) => (
          <div key={i} style={{ marginBottom: 12 }}>
            <p style={{
              fontFamily: 'Outfit, sans-serif', fontSize: 9, letterSpacing: '0.3em',
              textTransform: 'uppercase', color: 'rgba(122,90,24,0.7)', marginBottom: 3,
            }}>{lbl}</p>
            <p style={{
              fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic',
              color: '#3a2c0a', fontSize: 16, lineHeight: 1.45, whiteSpace: 'pre-wrap',
            }}>{answers[i] || '—'}</p>
          </div>
        ))}
        <p style={{
          fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic',
          textAlign: 'right', color: '#5a3f0a', fontSize: 14, marginTop: 18,
        }}>— signed by the Universe</p>
      </div>
      {/* Bottom gold rod */}
      <div style={{
        height: 18, marginTop: 0,
        background: 'linear-gradient(180deg, #F1D27A 0%, #C99A3D 50%, #7A5A18 100%)',
        borderRadius: '2px 2px 6px 6px',
        boxShadow: '0 -2px 4px rgba(0,0,0,0.3)',
        position: 'relative',
      }}>
        <div style={{ position: 'absolute', left: -7, bottom: -3, width: 16, height: 24, borderRadius: '50%',
          background: 'radial-gradient(circle at 35% 30%, #F1D27A, #B7892D 60%, #5a3f0a)',
          boxShadow: '0 2px 5px rgba(0,0,0,0.45)' }}/>
        <div style={{ position: 'absolute', right: -7, bottom: -3, width: 16, height: 24, borderRadius: '50%',
          background: 'radial-gradient(circle at 35% 30%, #F1D27A, #B7892D 60%, #5a3f0a)',
          boxShadow: '0 2px 5px rgba(0,0,0,0.45)' }}/>
      </div>
    </div>
  );
};

/* ─────────────── Pilgrimage path (destination-style 3-spot gold journey) ─── */

/* A curved gold line connects the user avatar (bottom) → genie's lamp
   (centre) → divine beam (top). A vibrant colorful light orb travels
   along the line, lighting up each waypoint as it passes. Mirrors a
   premium travel/destination map ("you-are-here → next stop → arrived"). */
const PilgrimagePath = ({ profilePic, nickname }) => {
  const vw = typeof window !== 'undefined' ? window.innerWidth  : 412;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 915;
  // User = bottom-LEFT · Genie/Lamp = MIDDLE-RIGHT · Divine = TOP-CENTRE
  const spotUser = { x: 70,           y: vh - 110 };
  const spotLamp = { x: vw - 70,      y: vh * 0.52 };
  const spotBeam = { x: vw / 2,       y: 90         };
  const pathD =
    `M ${spotUser.x} ${spotUser.y} ` +
    `Q ${spotUser.x + 80} ${spotLamp.y + 80}, ${spotLamp.x} ${spotLamp.y} ` +
    `Q ${spotLamp.x - 30} ${spotBeam.y + 80}, ${spotBeam.x} ${spotBeam.y}`;

  // Destiny-theme background twinkling sparkles — memoised so they don't
  // shuffle on re-render. Reduced from 50 → 22 stars for smoother rendering.
  const sparkles = React.useMemo(() =>
    [...Array(22)].map(() => ({
      x: Math.random() * vw,
      y: Math.random() * vh,
      size: 1 + Math.random() * 3.5,
      delay: Math.random() * 4,
      duration: 2.4 + Math.random() * 2.6,
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  , []);

  return (
    <div className="absolute inset-0 z-[90] pointer-events-none"
      style={{ animation: 'mfPilgFadeIn 1.4s cubic-bezier(0.22, 0.61, 0.36, 1) forwards' }}
      data-testid="manifest-pilgrimage">
      {/* ── DESTINY THEME: twinkling sparkles + drifting smoke veils ── */}
      <div className="absolute inset-0 overflow-hidden">
        {sparkles.map((s, i) => (
          <div key={i} className="absolute"
            style={{
              left: s.x, top: s.y,
              width: s.size, height: s.size,
              borderRadius: '50%',
              background: '#FFF6D6',
              boxShadow: `0 0 ${s.size * 4}px ${s.size * 2}px rgba(241,210,122,0.55)`,
              animation: `mfTwinkle ${s.duration}s ${s.delay}s ease-in-out infinite`,
              opacity: 0,
            }}
          />
        ))}
        {/* Soft drifting smoke patches in the background — plain blur
            instead of expensive SVG turbulence for smoother framerate. */}
        <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice"
          style={{ filter: 'blur(18px)', opacity: 0.4 }}>
          <defs>
            <radialGradient id="pilgSmoke" cx="50%" cy="50%" r="50%">
              <stop offset="0%"  stopColor="#7FB1FF" stopOpacity="0.55"/>
              <stop offset="50%" stopColor="#4A75D8" stopOpacity="0.25"/>
              <stop offset="100%" stopColor="#1E3A8A" stopOpacity="0"/>
            </radialGradient>
            <radialGradient id="pilgSmokeGold" cx="50%" cy="50%" r="50%">
              <stop offset="0%"  stopColor="#F1D27A" stopOpacity="0.45"/>
              <stop offset="60%" stopColor="#8B6B1F" stopOpacity="0.18"/>
              <stop offset="100%" stopColor="#3A2410" stopOpacity="0"/>
            </radialGradient>
          </defs>
          <ellipse cx={vw * 0.2} cy={vh * 0.3} rx={vw * 0.4} ry={vh * 0.2} fill="url(#pilgSmoke)"/>
          <ellipse cx={vw * 0.85} cy={vh * 0.3} rx={vw * 0.35} ry={vh * 0.18} fill="url(#pilgSmokeGold)"/>
          <ellipse cx={vw * 0.4} cy={vh * 0.75} rx={vw * 0.4} ry={vh * 0.22} fill="url(#pilgSmokeGold)"/>
          <ellipse cx={vw * 0.75} cy={vh * 0.85} rx={vw * 0.35} ry={vh * 0.18} fill="url(#pilgSmoke)"/>
        </svg>
      </div>

      {/* ── PREMIUM ROYAL GENIE — same figure used in chat, fully visible on
           the left side of the pilgrimage scene, floating with continuous
           smoke wisps surrounding it. ── */}
      <div className="absolute pointer-events-none"
        style={{
          top: vh * 0.45,
          left: 36,
          transform: 'translateY(-50%)',
          animation: 'mfGenieHover 4s ease-in-out infinite',
        }}>
        {/* Surrounding aura smoke */}
        <div className="absolute pointer-events-none"
          style={{
            top: -40, left: -40,
            width: 230, height: 280,
            animation: 'mfGenieAura 5s ease-in-out infinite',
          }}>
          <svg width="100%" height="100%" viewBox="0 0 230 280"
            style={{ filter: 'blur(10px)' }}>
            <defs>
              <radialGradient id="pilgGenieSmoke" cx="50%" cy="55%" r="55%">
                <stop offset="0%"  stopColor="#A6CCFF" stopOpacity="0.55"/>
                <stop offset="55%" stopColor="#4A75D8" stopOpacity="0.28"/>
                <stop offset="100%" stopColor="#1E3A8A" stopOpacity="0"/>
              </radialGradient>
            </defs>
            <ellipse cx="115" cy="140" rx="100" ry="120" fill="url(#pilgGenieSmoke)"/>
            <ellipse cx="60"  cy="90"  rx="40"  ry="55"  fill="url(#pilgGenieSmoke)"/>
            <ellipse cx="170" cy="170" rx="40"  ry="55"  fill="url(#pilgGenieSmoke)"/>
            <ellipse cx="115" cy="220" rx="60"  ry="36"  fill="url(#pilgGenieSmoke)"/>
          </svg>
        </div>
        {/* Rising smoke columns above the genie */}
        <div className="absolute pointer-events-none"
          style={{ bottom: 100, left: 30, width: 90, height: 160 }}>
          {[0, 1.8, 3.4].map((delay, i) => (
            <svg key={i} width="100%" height="100%" viewBox="0 0 90 160"
              style={{
                position: 'absolute', left: 0, top: 0,
                filter: 'blur(8px)',
                animation: `mfGenieSmokeRise 5s ${delay}s ease-out infinite`,
                opacity: 0,
              }}>
              <defs>
                <radialGradient id={`pilgFGenieRise${i}`} cx="50%" cy="60%" r="50%">
                  <stop offset="0%"  stopColor="#E6F1FF" stopOpacity="0.6"/>
                  <stop offset="60%" stopColor="#7FB1FF" stopOpacity="0.28"/>
                  <stop offset="100%" stopColor="#1E3A8A" stopOpacity="0"/>
                </radialGradient>
              </defs>
              <ellipse cx="45" cy="130" rx="26" ry="32" fill={`url(#pilgFGenieRise${i})`}/>
              <ellipse cx="45" cy="80"  rx="22" ry="36" fill={`url(#pilgFGenieRise${i})`}/>
              <ellipse cx="45" cy="30"  rx="16" ry="26" fill={`url(#pilgFGenieRise${i})`}/>
            </svg>
          ))}
        </div>
        {/* The premium royal genie — same as the chat figure (full body, all jewels) */}
        <svg width="150" height="232" viewBox="0 0 220 340"
          style={{
            position: 'relative',
            filter: 'drop-shadow(0 0 28px rgba(127,177,255,0.7)) drop-shadow(0 0 64px rgba(241,210,122,0.4))',
          }}>
          <defs>
            <linearGradient id="pgSkin" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%"  stopColor="#F5FAFF" stopOpacity="0.94"/>
              <stop offset="35%" stopColor="#B5D6FF" stopOpacity="0.88"/>
              <stop offset="75%" stopColor="#5E86D8" stopOpacity="0.78"/>
              <stop offset="100%" stopColor="#2A4DA0" stopOpacity="0.4"/>
            </linearGradient>
            <linearGradient id="pgBody" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%"  stopColor="#7FB1FF" stopOpacity="0.88"/>
              <stop offset="55%" stopColor="#3A5BC0" stopOpacity="0.72"/>
              <stop offset="100%" stopColor="#1E3A8A" stopOpacity="0.0"/>
            </linearGradient>
            <linearGradient id="pgTurban" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"  stopColor="#FFF6D6"/>
              <stop offset="35%" stopColor="#F1D27A"/>
              <stop offset="100%" stopColor="#7A5418"/>
            </linearGradient>
            <linearGradient id="pgVest" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"  stopColor="#F1D27A"/>
              <stop offset="100%" stopColor="#7A5418"/>
            </linearGradient>
            <radialGradient id="pgRuby" cx="38%" cy="32%" r="60%">
              <stop offset="0%"  stopColor="#FFD9D9"/>
              <stop offset="40%" stopColor="#E63946"/>
              <stop offset="100%" stopColor="#5A0A14"/>
            </radialGradient>
          </defs>
          <circle cx="110" cy="92" r="92" fill="rgba(241,210,122,0.12)"/>
          <circle cx="110" cy="92" r="66" fill="rgba(241,210,122,0.10)"/>
          {/* Turban — three wraps */}
          <path d="M 36 92 Q 110 28, 184 92 Q 168 110, 110 110 Q 52 110, 36 92 Z"
            fill="url(#pgTurban)" stroke="#5A4012" strokeWidth="0.9"/>
          <path d="M 50 82 Q 110 38, 170 82 Q 154 96, 110 96 Q 66 96, 50 82 Z"
            fill="url(#pgTurban)" opacity="0.92" stroke="#5A4012" strokeWidth="0.6"/>
          <path d="M 64 70 Q 110 38, 156 70 Q 140 84, 110 84 Q 80 84, 64 70 Z"
            fill="url(#pgTurban)" stroke="#5A4012" strokeWidth="0.5"/>
          {/* Ruby brooch */}
          <ellipse cx="110" cy="58" rx="9" ry="6" fill="url(#pgRuby)" stroke="#5A4012" strokeWidth="0.6"/>
          <ellipse cx="107" cy="56" rx="3" ry="1.6" fill="rgba(255,230,230,0.85)"/>
          <g stroke="#5A4012" fill="url(#pgTurban)" strokeWidth="0.4">
            <circle cx="100" cy="58" r="1.6"/><circle cx="120" cy="58" r="1.6"/>
            <circle cx="110" cy="52" r="1.6"/><circle cx="110" cy="64" r="1.6"/>
          </g>
          {/* Plume feather */}
          <path d="M 110 50 C 124 30, 142 22, 156 28 C 154 34, 148 40, 140 44 C 134 46, 124 48, 116 48 C 112 48, 110 49, 110 50 Z"
            fill="url(#pgTurban)" stroke="#5A4012" strokeWidth="0.5"/>
          {/* Head + jaw shadow */}
          <path d="M 84 108 C 84 100, 90 94, 110 92 C 130 94, 136 100, 136 108 C 136 124, 130 134, 122 138 C 116 142, 104 142, 98 138 C 90 134, 84 124, 84 108 Z"
            fill="url(#pgSkin)"/>
          <path d="M 90 122 C 100 130, 120 130, 130 122 C 126 138, 114 142, 110 142 C 106 142, 94 138, 90 122 Z"
            fill="rgba(42,77,160,0.28)" opacity="0.7"/>
          <path d="M 94 108 Q 110 102, 126 108" stroke="rgba(42,77,160,0.4)" strokeWidth="1" fill="none" strokeLinecap="round" opacity="0.7"/>
          {/* Neck */}
          <path d="M 100 142 Q 100 152, 102 158 Q 110 161, 118 158 Q 120 152, 120 142 Q 110 145, 100 142 Z"
            fill="url(#pgSkin)" opacity="0.94"/>
          {/* Sapphire necklace */}
          <path d="M 70 158 Q 110 168, 150 158" stroke="url(#pgVest)" strokeWidth="1.2" fill="none"/>
          {[80, 92, 104, 116, 128, 140].map((cx, i) => (
            <circle key={i} cx={cx} cy={161} r="1.4" fill="url(#pgVest)" stroke="#5A4012" strokeWidth="0.3"/>
          ))}
          <path d="M 105 168 L 115 168 L 117 172 L 110 180 L 103 172 Z"
            fill="#0F52BA" stroke="#5A4012" strokeWidth="0.5"/>
          <path d="M 105 168 L 115 168 L 110 174 Z" fill="rgba(255,255,255,0.6)"/>
          {/* Shoulders + torso */}
          <path d="M 56 168 C 64 156, 82 152, 102 156 L 110 158 L 118 156 C 138 152, 156 156, 164 168 C 170 180, 172 196, 168 210 C 156 218, 130 220, 110 220 C 90 220, 64 218, 52 210 C 48 196, 50 180, 56 168 Z"
            fill="url(#pgSkin)"/>
          {/* Gold vest panels */}
          <path d="M 78 162 C 84 174, 92 180, 100 184 L 100 222 C 90 224, 80 220, 76 214 C 72 200, 73 178, 78 162 Z"
            fill="url(#pgVest)" stroke="#5A4012" strokeWidth="0.6" opacity="0.95"/>
          <path d="M 142 162 C 136 174, 128 180, 120 184 L 120 222 C 130 224, 140 220, 144 214 C 148 200, 147 178, 142 162 Z"
            fill="url(#pgVest)" stroke="#5A4012" strokeWidth="0.6" opacity="0.95"/>
          {/* Ruby vest buttons */}
          <circle cx="92" cy="184" r="1.8" fill="url(#pgRuby)" stroke="#5A4012" strokeWidth="0.3"/>
          <circle cx="92" cy="204" r="1.8" fill="url(#pgRuby)" stroke="#5A4012" strokeWidth="0.3"/>
          <circle cx="128" cy="184" r="1.8" fill="url(#pgRuby)" stroke="#5A4012" strokeWidth="0.3"/>
          <circle cx="128" cy="204" r="1.8" fill="url(#pgRuby)" stroke="#5A4012" strokeWidth="0.3"/>
          {/* Gold sash with emerald buckle */}
          <path d="M 64 216 Q 110 224, 156 216 L 158 228 Q 110 234, 62 228 Z"
            fill="url(#pgVest)" stroke="#5A4012" strokeWidth="0.6"/>
          <path d="M 64 216 Q 110 220, 156 216" stroke="rgba(255,255,255,0.45)" strokeWidth="1.4" fill="none"/>
          <ellipse cx="110" cy="222" rx="9" ry="4.5" fill="url(#pgVest)" stroke="#5A4012" strokeWidth="0.6"/>
          <ellipse cx="110" cy="222" rx="6" ry="3" fill="#10B981" stroke="#063A26" strokeWidth="0.4"/>
          <ellipse cx="108" cy="220.5" rx="2.2" ry="1.2" fill="rgba(220,255,235,0.7)"/>
          {/* Lower body fades to smoke */}
          <path d="M 54 228 Q 110 240, 166 228 L 172 280 Q 110 290, 48 280 Z"
            fill="url(#pgBody)" opacity="0.92"/>
          <path d="M 60 280 Q 92 320, 88 340 L 132 340 Q 128 320, 160 280 Q 110 292, 60 280 Z"
            fill="url(#pgBody)" opacity="0.55" style={{ filter: 'blur(6px)' }}/>
          <ellipse cx="110" cy="334" rx="48" ry="7"
            fill="url(#pgBody)" opacity="0.35"
            style={{ filter: 'blur(8px)' }}/>
        </svg>
      </div>

      {/* ── USER avatar — START waypoint (88px) ── */}
      <Waypoint x={spotUser.x} y={spotUser.y} label="You" pulseDelay={0} size={88}>
        {profilePic ? (
          <img src={profilePic} alt={nickname} className="w-full h-full object-cover" draggable={false}/>
        ) : (
          <div className="w-full h-full flex items-center justify-center font-['Cormorant_Garamond'] italic"
            style={{ color: GOLD_LIGHT, fontSize: 38 }}>{(nickname || 'A')[0].toUpperCase()}</div>
        )}
      </Waypoint>

      {/* ── GENIE'S LAMP — MID waypoint (88px) ── */}
      <Waypoint x={spotLamp.x} y={spotLamp.y} label="Genie's Lamp" pulseDelay={1.0} size={88}>
        <div className="w-full h-full relative flex items-center justify-center overflow-hidden"
          style={{ background: 'radial-gradient(circle, #1a0d04, #04030a)' }}>
          <div className="absolute pointer-events-none" style={{
            top: '-15%', left: '-10%', width: '55%', height: '70%',
            background: 'radial-gradient(circle, rgba(166,204,255,0.6), transparent 70%)',
            filter: 'url(#smokeTurbulence) blur(1px)',
            opacity: 0.85,
            animation: 'mfGenieAura 4s ease-in-out infinite',
          }}/>
          <GoldLamp size={72}/>
        </div>
      </Waypoint>

      {/* ── DIVINE — DESTINATION waypoint (88px) shapeless breathing light ── */}
      <Waypoint x={spotBeam.x} y={spotBeam.y} label="The Divine" pulseDelay={2.0} size={88}>
        <div className="w-full h-full relative overflow-hidden"
          style={{ background: '#040106' }}>
          <div className="absolute inset-0" style={{
            background: 'radial-gradient(circle at 50% 50%, rgba(255,255,255,1) 0%, rgba(255,246,214,0.85) 22%, rgba(241,210,122,0.45) 55%, transparent 80%)',
            animation: 'mfDivineBreathe 2.6s ease-in-out infinite',
            filter: 'blur(1.5px)',
          }}/>
          <div className="absolute inset-0" style={{
            background: 'radial-gradient(circle at 50% 50%, rgba(255,255,255,0.95) 0%, transparent 50%)',
            mixBlendMode: 'screen',
            animation: 'mfDivineBreathe 2.6s 0.4s ease-in-out infinite',
          }}/>
        </div>
      </Waypoint>

      {/* ── THE GOLD TRAVEL LINE + WHITE ENERGY ORB (SVG animateMotion) ── */}
      <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: 'none' }}>
        <defs>
          <linearGradient id="travelLine" x1="0" y1="1" x2="0" y2="0">
            <stop offset="0%"  stopColor="#F1D27A" stopOpacity="0.45"/>
            <stop offset="50%" stopColor="#FFF6D6" stopOpacity="1"/>
            <stop offset="100%" stopColor="#F1D27A" stopOpacity="0.45"/>
          </linearGradient>
          <radialGradient id="travelOrb" cx="50%" cy="50%" r="50%">
            <stop offset="0%"  stopColor="#FFFFFF"/>
            <stop offset="30%" stopColor="#FFF6D6"/>
            <stop offset="70%" stopColor="#F1D27A"/>
            <stop offset="100%" stopColor="rgba(241,210,122,0)"/>
          </radialGradient>
          {/* Named path that animateMotion references */}
          <path id="manifestTravelPath" d={pathD}/>
        </defs>

        {/* Wide soft glow underlay — draws over 1.2s starting immediately */}
        <use href="#manifestTravelPath"
          fill="none"
          stroke="rgba(255,246,214,0.55)"
          strokeWidth="16"
          strokeLinecap="round"
          strokeDasharray="1600"
          style={{
            filter: 'blur(10px)',
            animation: 'mfPathDraw 1.2s 0.2s cubic-bezier(0.22, 0.61, 0.36, 1) forwards',
            opacity: 0,
          }}
        />
        {/* Thicker dashed gold path — completes by ~1.6s */}
        <use href="#manifestTravelPath"
          fill="none"
          stroke="url(#travelLine)"
          strokeWidth="5"
          strokeDasharray="10 14"
          strokeLinecap="round"
          style={{
            animation: 'mfPathDraw 1.2s 0.4s cubic-bezier(0.22, 0.61, 0.36, 1) forwards',
            filter: 'drop-shadow(0 0 12px rgba(241,210,122,0.85))',
            opacity: 0,
          }}
        />

        {/* WHITE ENERGY orb — main travelling soul.
            Begins at 1.8s — AFTER the path has fully drawn — so the
            order is strictly: scene fade-in → path appears → orb flows.
            Uses keyPoints with a brief linger at the Genie midpoint
            so the energy clearly passes USER → GENIE → DIVINE. */}
        <circle r="22" fill="url(#travelOrb)"
          style={{
            filter: 'drop-shadow(0 0 22px #FFFFFF) drop-shadow(0 0 44px #F1D27A)',
          }}>
          <animateMotion
            dur="5.4s"
            begin="1.8s"
            fill="freeze"
            rotate="auto"
            calcMode="spline"
            keyTimes="0;0.42;0.58;1"
            keyPoints="0;0.5;0.5;1"
            keySplines="0.4 0 0.2 1;0 0 1 1;0.4 0 0.2 1"
            path={pathD}
          />
          {/* opacity ramp — smooth fade-in, hold, gentle settle */}
          <animate attributeName="opacity"
            values="0;1;1;0.95"
            keyTimes="0;0.1;0.92;1"
            dur="5.4s" begin="1.8s" fill="freeze"/>
          {/* gentle pulse as it passes each waypoint */}
          <animate attributeName="r"
            values="22;26;22;28;22;30"
            keyTimes="0;0.42;0.5;0.58;0.85;1"
            dur="5.4s" begin="1.8s" fill="freeze"/>
        </circle>

        {/* Trail comets — follow the same smooth eased path, staggered. */}
        <circle r="12" fill="url(#travelOrb)" style={{ mixBlendMode: 'screen' }}>
          <animateMotion
            dur="5.4s" begin="2.05s" fill="freeze"
            calcMode="spline"
            keyTimes="0;0.42;0.58;1"
            keyPoints="0;0.5;0.5;1"
            keySplines="0.4 0 0.2 1;0 0 1 1;0.4 0 0.2 1"
            path={pathD}/>
          <animate attributeName="opacity" values="0;0.85;0.85;0" keyTimes="0;0.15;0.9;1" dur="5.4s" begin="2.05s" fill="freeze"/>
        </circle>
        <circle r="7" fill="url(#travelOrb)" style={{ mixBlendMode: 'screen' }}>
          <animateMotion
            dur="5.4s" begin="2.3s" fill="freeze"
            calcMode="spline"
            keyTimes="0;0.42;0.58;1"
            keyPoints="0;0.5;0.5;1"
            keySplines="0.4 0 0.2 1;0 0 1 1;0.4 0 0.2 1"
            path={pathD}/>
          <animate attributeName="opacity" values="0;0.7;0.7;0" keyTimes="0;0.15;0.9;1" dur="5.4s" begin="2.3s" fill="freeze"/>
        </circle>
      </svg>
    </div>
  );
};

/* Each circular waypoint on the journey. */
const Waypoint = ({ x, y, label, pulseDelay = 0, size = 60, children }) => {
  return (
    <div className="absolute" style={{
      left: x - size / 2, top: y - size / 2,
      width: size, height: size, borderRadius: '50%',
      overflow: 'hidden',
      background: '#0a0610',
      border: `2.5px solid ${GOLD}`,
      boxShadow: `0 0 24px rgba(241,210,122,0.8), 0 0 48px rgba(241,210,122,0.4)`,
    }}>
      {children}
      <div className="absolute"
        style={{
          left: -10, top: -10, right: -10, bottom: -10,
          borderRadius: '50%',
          border: `2px solid ${GOLD_LIGHT}`,
          animation: `mfWaypointPulse 1.0s ${pulseDelay}s ease-out 2 forwards`,
          opacity: 0,
        }}/>
      <div className="absolute"
        style={{
          left: '50%', top: '100%',
          transform: 'translateX(-50%)',
          marginTop: 10,
          fontFamily: 'Outfit, sans-serif',
          fontSize: 9.5, letterSpacing: '0.32em',
          textTransform: 'uppercase',
          color: GOLD_LIGHT,
          whiteSpace: 'nowrap',
          textShadow: '0 0 10px rgba(0,0,0,0.85), 0 0 14px rgba(241,210,122,0.4)',
        }}>
        {label}
      </div>
    </div>
  );
};

export default TheManifest;
